/**
 * Helpdesk — Agent Inbox + Ticket Detail + Settings.
 * Iter 97 Phase A. Multi-brand scoped everywhere.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import CommentThread from "@/components/CommentThread";
import CallButton from "@/components/CallButton";
import SmartRichText from "@/components/SmartRichText";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import AdvancedTable from "@/components/table/AdvancedTable";
import { copyText } from "@/lib/clipboard";
import { fmtRelative } from "@/lib/format";
import {
  Ticket, ArrowLeft, Users, Settings2, Zap, AlertTriangle, Clock, Star,
  ChevronRight, Filter, Plus, Link2, Copy, Trash2, RefreshCw, Eye, Pencil,
  Paperclip, Timer, GitMerge, Split, CheckSquare, Square, Download, Upload, X,
  ArrowUp, ArrowDown, Archive, ArchiveRestore, Check as CheckIcon,
  MessageSquare, History, TrendingUp,
} from "lucide-react";

const PRIORITY_TONES = {
  urgent: "border-red-500/40 bg-red-500/10 text-red-700",
  high:   "border-orange-500/40 bg-orange-500/10 text-orange-700",
  normal: "border-border bg-muted text-muted-foreground",
  low:    "border-blue-500/30 bg-blue-500/10 text-blue-700",
};
const STATUS_TONES = {
  new: "bg-blue-500/10 text-blue-700 border-blue-500/30",
  open: "bg-primary/10 text-primary border-primary/30",
  in_progress: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  waiting: "bg-purple-500/10 text-purple-700 border-purple-500/30",
  resolved: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  closed: "bg-muted text-muted-foreground border-border",
  reopened: "bg-red-500/10 text-red-700 border-red-500/30",
};


// ─────────────────────── At-risk strip (used by inbox) ───────────────────────

function AtRiskStrip({ wsId }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/helpdesk/dashboard`)
      .then((r) => setRows(r.data.at_risk || []))
      .catch(() => setRows([]));
  }, [wsId]);
  if (!rows.length) return null;
  return (
    <Card className="rounded-none border-red-300 bg-red-50/40" data-testid="hd-at-risk-strip">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <span className="text-[11px] uppercase tracking-widest font-semibold text-red-700">
            {rows.length} ticket{rows.length === 1 ? "" : "s"} at risk
          </span>
          <button className="ml-auto text-[11px] underline text-red-600"
                   onClick={() => setOpen((v) => !v)}
                   data-testid="hd-at-risk-toggle">
            {open ? "Hide" : "Show"}
          </button>
        </div>
        {open && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {rows.slice(0, 9).map((t) => (
              <Link key={t.id} to={`/app/helpdesk/tickets/${t.id}`}
                     className="border border-red-200 bg-background hover:bg-red-50 p-2 text-xs"
                     data-testid={`hd-at-risk-item-${t.id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-mono text-muted-foreground">{t.ticket_number}</span>
                  <span className={`text-[9px] uppercase font-semibold px-1.5 border ${PRIORITY_TONES[t.priority] || ""}`}>{t.priority}</span>
                  {t.ai_sentiment && t.ai_sentiment !== "neutral" && (
                    <span className="text-[9px]">🎭 {t.ai_sentiment}</span>
                  )}
                </div>
                <div className="font-semibold mt-0.5 truncate">{t.subject}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {t.assignee_user_id ? "Assigned" : "Unassigned"} · SLA {t.sla_resolve_due_at ? new Date(t.sla_resolve_due_at).toLocaleString() : "n/a"}
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}



function TemplatePicker({ wsId, onPick }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("");
  const load = async () => {
    try {
      const r = await api.get(`/workspaces/${wsId}/helpdesk/templates`);
      setRows(r.data || []);
      setLoaded(true);
    } catch { /* noop */ }
  };
  useEffect(() => { if (open && !loaded) load(); }, [open]); // eslint-disable-line
  const filtered = rows.filter((r) => !filter.trim()
    || r.name.toLowerCase().includes(filter.toLowerCase())
    || (r.shortcut || "").toLowerCase().includes(filter.toLowerCase()));
  return (
    <div className="relative">
      <Button type="button" size="sm" variant="outline" className="rounded-none h-8 text-xs"
              onClick={() => setOpen((v) => !v)} data-testid="ticket-templates-btn">
        📋 Templates
      </Button>
      {open && (
        <div className="absolute z-30 top-9 left-0 w-80 bg-background border border-border shadow-lg p-2"
             data-testid="ticket-templates-popover">
          <Input className="h-7 text-xs mb-2" placeholder="Search templates or /shortcut"
                 value={filter} onChange={(e) => setFilter(e.target.value)} autoFocus />
          <div className="max-h-64 overflow-y-auto divide-y divide-border">
            {filtered.length === 0 ? (
              <div className="text-xs text-muted-foreground p-3 text-center">
                {rows.length === 0 ? "No templates. Create some in Settings → Templates." : "No matches."}
              </div>
            ) : filtered.map((t) => (
              <button key={t.id} type="button"
                       className="w-full text-left p-2 hover:bg-muted/50 text-xs"
                       data-testid={`ticket-templates-item-${t.id}`}
                       onClick={() => { onPick(t.body); setOpen(false); }}>
                <div className="font-semibold flex items-center gap-2">
                  {t.name}
                  {t.shortcut && <span className="text-[9px] font-mono bg-muted px-1">/{t.shortcut}</span>}
                </div>
                <div className="text-muted-foreground line-clamp-2 mt-0.5">{t.body}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



// ═════════════════════════ Bulk actions bar ═════════════════════════
function BulkActionsBar({ wsId, selectedIds, queues, members, onDone, onClear }) {
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const run = async (action, value) => {
    if (!selectedIds.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/helpdesk/tickets/bulk`, {
        ticket_ids: selectedIds, action, value: value || null,
      });
      if (data.failed > 0) {
        toast.warning(`${data.ok} succeeded · ${data.failed} failed`);
      } else {
        toast.success(`${data.ok} tickets updated`);
      }
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk action failed")); }
    finally { setBusy(false); }
  };
  // Iter 253z-6 · Dedicated bulk-snooze + bulk-transfer endpoints (they need
  // richer payloads than the generic /bulk shape).
  const runTransfer = async (queueId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/helpdesk/tickets/bulk-transfer`,
                                             { ticket_ids: selectedIds, queue_id: queueId });
      toast.success(`Transferred ${data.transferred} → ${data.queue}`
                        + (data.failed ? ` · ${data.failed} failed` : ""));
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Transfer failed")); }
    finally { setBusy(false); }
  };
  const runSnooze = async (isoWake, reason) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/helpdesk/tickets/bulk-snooze`,
                                             { ticket_ids: selectedIds,
                                                snoozed_until: isoWake, reason });
      toast.success(`Snoozed ${data.snoozed}`
                        + (data.failed ? ` · ${data.failed} failed` : ""));
      setSnoozeOpen(false);
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Snooze failed")); }
    finally { setBusy(false); }
  };
  return (
    <>
    <Card className="rounded-none border-primary/40 bg-primary/5" data-testid="hd-bulk-bar">
      <CardContent className="p-3 flex flex-wrap items-center gap-2">
        <div className="text-xs font-semibold">{selectedIds.length} selected</div>
        <div className="mx-2 h-4 w-px bg-border" />
        <select
          className="h-8 px-2 border border-border text-xs bg-background"
          defaultValue=""
          onChange={(e) => { if (e.target.value) run("status", e.target.value); e.target.value = ""; }}
          disabled={busy}
          data-testid="hd-bulk-status"
        >
          <option value="">Set status…</option>
          {["open", "in_progress", "waiting", "resolved", "closed"].map((s) => (
            <option key={s} value={s}>{s.replace("_", " ")}</option>
          ))}
        </select>
        <select
          className="h-8 px-2 border border-border text-xs bg-background"
          defaultValue=""
          onChange={(e) => { if (e.target.value) run("priority", e.target.value); e.target.value = ""; }}
          disabled={busy}
          data-testid="hd-bulk-priority"
        >
          <option value="">Set priority…</option>
          {["low", "normal", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select
          className="h-8 px-2 border border-border text-xs bg-background"
          defaultValue=""
          onChange={(e) => { if (e.target.value !== "") run("assign", e.target.value === "__unassign__" ? "" : e.target.value); e.target.value = ""; }}
          disabled={busy}
          data-testid="hd-bulk-assign"
        >
          <option value="">Assign to…</option>
          <option value="__unassign__">— Unassign —</option>
          {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
        </select>
        <select
          className="h-8 px-2 border border-border text-xs bg-background"
          defaultValue=""
          onChange={(e) => { const v = e.target.value; if (v) runTransfer(v); e.target.value = ""; }}
          disabled={busy || !queues?.length}
          data-testid="hd-bulk-transfer"
        >
          <option value="">Transfer queue…</option>
          {(queues || []).map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
        </select>
        <Button
          size="sm" variant="outline" className="rounded-none h-8 text-xs"
          onClick={() => setSnoozeOpen(true)}
          disabled={busy}
          data-testid="hd-bulk-snooze"
        >
          <Clock className="w-3 h-3 mr-1" /> Snooze
        </Button>
        <Button
          size="sm" variant="outline" className="rounded-none h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
          onClick={() => setConfirmClose(true)}
          disabled={busy}
          data-testid="hd-bulk-close"
        >
          Close selected
        </Button>
        <Button size="sm" variant="ghost" className="rounded-none h-8 text-xs ml-auto" onClick={onClear} data-testid="hd-bulk-clear">
          <X className="w-3 h-3 mr-1" /> Clear
        </Button>
      </CardContent>
    </Card>
    <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
      <AlertDialogContent data-testid="hd-bulk-close-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>Close {selectedIds.length} {selectedIds.length === 1 ? "ticket" : "tickets"}?</AlertDialogTitle>
          <AlertDialogDescription>
            Closed tickets stop receiving replies from customers. This action can be reversed by re-opening each ticket.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="hd-bulk-close-cancel">Keep open</AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
            onClick={() => { setConfirmClose(false); run("status", "closed"); }}
            data-testid="hd-bulk-close-confirm-btn"
          >
            Close them
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    {snoozeOpen && (
      <BulkSnoozeDialog count={selectedIds.length}
                            onClose={() => setSnoozeOpen(false)}
                            onSubmit={runSnooze} />
    )}
    </>
  );
}


// ═════════════════════════ Inbox ═════════════════════════

export default function HelpdeskInbox() {
  const { activeWorkspaceId: wsId, user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [queues, setQueues] = useState([]);
  // Feb 2026 R292u · Default view = "My Open" — the agent who just
  // logged in sees ONLY the open tickets routed to them, instead of
  // the full queue. The URL ?view=all opt-out keeps triage shortcuts
  // alive for team leads who prefer the global open list on load.
  const [filters, setFilters] = useState({ status: "new,open,in_progress,waiting,reopened", queue_id: "", priority: "", q: "", snooze: "exclude", assignee_user_id: "", sla_risk: false });
  const [defaultApplied, setDefaultApplied] = useState(false);
  useEffect(() => {
    if (defaultApplied) return;
    const uid = user?.id;
    if (!uid) return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("view") === "all") { setDefaultApplied(true); return; }
    setFilters((f) => ({ ...f, assignee_user_id: uid }));
    setDefaultApplied(true);
  }, [user, defaultApplied]);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [members, setMembers] = useState([]);
  const nav = useNavigate();

  const load = async () => {
    if (!wsId) return;
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      if (filters.queue_id) params.set("queue_id", filters.queue_id);
      if (filters.priority) params.set("priority", filters.priority);
      if (filters.q) params.set("q", filters.q);
      if (filters.assignee_user_id) params.set("assignee_user_id", filters.assignee_user_id);
      if (filters.snooze === "exclude") params.set("exclude_snoozed", "1");
      else if (filters.snooze === "only") params.set("snoozed_only", "1");
      const [s, t, q, m] = await Promise.all([
        api.get(`/workspaces/${wsId}/helpdesk/summary`),
        api.get(`/workspaces/${wsId}/helpdesk/tickets?${params.toString()}`),
        api.get(`/workspaces/${wsId}/helpdesk/queues`),
        api.get(`/workspaces/${wsId}/members`).catch(() => ({ data: [] })),
      ]);
      let rows = t.data;
      // Iter 253z-6 · Client-side SLA-at-risk filter — no backend param
      // (SLA thresholds are per-queue and would require joining). Simple:
      // sla_resolve_due_at within the next 4 hours or already breached.
      if (filters.sla_risk) {
        const now = Date.now();
        const soon = now + 4 * 3600_000;
        rows = rows.filter((r) => {
          const due = r.sla_resolve_due_at ? new Date(r.sla_resolve_due_at).getTime() : null;
          if (!due || r.resolved_at) return false;
          return due < soon;
        });
      }
      if (filters.__unassigned_only) {
        rows = rows.filter((r) => !r.assignee_user_id);
      }
      setSummary(s.data); setTickets(rows); setQueues(q.data); setMembers(m.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load helpdesk")); }
  };
  useEffect(() => { load(); }, [wsId, filters.status, filters.queue_id, filters.priority, filters.snooze, filters.assignee_user_id, filters.sla_risk, filters.__unassigned_only]);

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="helpdesk-inbox-page">
      <PageHeader
        title="Helpdesk"
        subtitle="Tickets, queues, SLAs — one inbox for every conversation."
        icon={Ticket}
        actions={
          <div className="flex gap-2">
            <Link to="/app/helpdesk/intelligence">
              <Button variant="outline" size="sm" className="rounded-none" data-testid="helpdesk-intel-btn"><TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Intelligence</Button>
            </Link>
            <Link to="/app/helpdesk/settings">
              <Button variant="outline" size="sm" className="rounded-none" data-testid="helpdesk-settings-btn"><Settings2 className="w-3.5 h-3.5 mr-1.5" /> Settings</Button>
            </Link>
            <Button size="sm" className="rounded-none" onClick={() => setShowCreate(true)} data-testid="helpdesk-new-ticket-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> New ticket</Button>
          </div>
        }
      />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryTile label="Open" value={summary.total_open} icon={Ticket} testid="hd-tile-open" />
          <SummaryTile label="Unassigned" value={summary.unassigned} icon={Users} tone={summary.unassigned > 0 ? "warn" : null} testid="hd-tile-unassigned" />
          <SummaryTile label="SLA overdue" value={summary.overdue} icon={AlertTriangle} tone={summary.overdue > 0 ? "danger" : null} testid="hd-tile-overdue" />
          <SummaryTile label="Resolved today" value={summary.resolved_today} icon={Zap} tone={summary.resolved_today > 0 ? "ok" : null} testid="hd-tile-resolved" />
        </div>
      )}

      <AtRiskStrip wsId={wsId} />

      {/* Iter 253z-6 · Saved-view presets */}
      <HelpdeskViewPresets wsId={wsId} filters={filters} setFilters={setFilters}
                              queues={queues} meId={user?.id} />

      {/* Filters */}
      <Card className="rounded-none">
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          <select className="h-8 px-2 border border-border text-xs bg-background" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="hd-filter-status">
            <option value="new,open,in_progress,waiting,reopened">Open (all)</option>
            <option value="new">New only</option>
            <option value="open,in_progress">Active</option>
            <option value="waiting">Waiting</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <select className="h-8 px-2 border border-border text-xs bg-background" value={filters.queue_id} onChange={(e) => setFilters({ ...filters, queue_id: e.target.value })} data-testid="hd-filter-queue">
            <option value="">All queues</option>
            {queues.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
          </select>
          <select className="h-8 px-2 border border-border text-xs bg-background" value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} data-testid="hd-filter-priority">
            <option value="">Any priority</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
          <select className="h-8 px-2 border border-border text-xs bg-background" value={filters.snooze} onChange={(e) => setFilters({ ...filters, snooze: e.target.value })} data-testid="hd-filter-snooze">
            <option value="exclude">Active (hide snoozed)</option>
            <option value="">Include snoozed</option>
            <option value="only">Snoozed only</option>
          </select>
          <Input className="h-8 flex-1 min-w-[160px] rounded-none" placeholder="Search subject or #ID"
                 value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 data-testid="hd-filter-q" />
          <Button size="sm" variant="ghost" className="rounded-none h-8" onClick={load}>Refresh</Button>
        </CardContent>
      </Card>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <BulkActionsBar
          wsId={wsId}
          selectedIds={Array.from(selected)}
          queues={queues}
          members={members}
          onDone={() => { setSelected(new Set()); load(); }}
          onClear={() => setSelected(new Set())}
        />
      )}

      {/* Ticket list */}
      <Card className="rounded-none">
        <CardContent className="p-0">
          {tickets.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground" data-testid="hd-empty">
              <Ticket className="w-8 h-8 mx-auto mb-2 text-muted-foreground/60" />
              No tickets match these filters. When your team creates one or a client submits via the widget, it&apos;ll appear here.
            </div>
          ) : (
            <TicketsSmartTable tickets={tickets} selected={selected}
                                     setSelected={setSelected} nav={nav}
                                     wsId={wsId} />
          )}
        </CardContent>
      </Card>

      {showCreate && (
        <NewTicketDialog wsId={wsId} queues={queues} onClose={() => setShowCreate(false)}
                          onCreated={(id) => { setShowCreate(false); nav(`/app/helpdesk/tickets/${id}`); }} />
      )}
    </div>
  );
}

// Iter 253x · Ticket list now uses the AdvancedTable so agents get the same
// customisable columns / sort / filter / group / crop / export experience as
// the Clients page. All ticket fields are present as columns; the row-level
// "quick preview" action opens an inline drawer with the last comments +
// activity so agents can scan progress without a full page nav.
function TicketsSmartTable({ tickets, selected, setSelected, nav, wsId }) {
  const [preview, setPreview] = useState(null);   // ticket id
  const [members, setMembers] = useState([]);
  const [queues, setQueues] = useState([]);
  React.useEffect(() => {
    if (!wsId) return;
    Promise.all([
      api.get(`/workspaces/${wsId}/members`).then((r) => setMembers(r.data || [])).catch(() => {}),
      api.get(`/workspaces/${wsId}/helpdesk/queues`).then((r) => setQueues(r.data || [])).catch(() => {}),
    ]);
  }, [wsId]);
  // Iter 253z-5 · Inline patch helper — fires PATCH and refreshes the row.
  const inlinePatch = async (t, field, value) => {
    try {
      if (field === "assignee_user_id") {
        await api.post(`/helpdesk/tickets/${t.id}/assign`,
                             { assignee_user_id: value || null });
      } else if (field === "status") {
        await api.post(`/helpdesk/tickets/${t.id}/transition`, { status: value });
      } else {
        await api.patch(`/helpdesk/tickets/${t.id}`, { [field]: value || null });
      }
      // Mutate in place so table doesn't have to re-fetch.
      t[field] = value || null;
      if (field === "assignee_user_id") {
        const m = members.find((mm) => mm.user_id === value);
        t.assignee_name = m ? (m.name || m.email) : null;
      }
      if (field === "queue_id") {
        const q = queues.find((qq) => qq.id === value);
        t.queue_name = q ? q.name : null;
      }
      toast.success("Updated");
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const columns = [
    { key: "ticket_number", label: "#", width: 90, sortable: true, filterType: "text", frozen: true,
      render: (t) => <span className="text-[10px] font-mono text-muted-foreground">{t.ticket_number}</span> },
    { key: "subject", label: "Subject", width: 320, sortable: true, filterType: "text",
      render: (t) => (
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{t.subject}</div>
          <div className="text-[11px] text-muted-foreground truncate">
            From {t.requester_name || t.requester_email || "internal"}
          </div>
        </div>
      ) },
    { key: "status", label: "Status", width: 130, sortable: true, groupable: true, filterType: "select",
      filterOptions: ["new","open","in_progress","waiting","resolved","closed","reopened"].map((v) => ({ value: v, label: v })),
      render: (t) => (
        <select onClick={(e) => e.stopPropagation()}
                value={t.status || ""}
                onChange={(e) => inlinePatch(t, "status", e.target.value)}
                className={`text-[10px] uppercase font-semibold px-1 py-0.5 border bg-background ${STATUS_TONES[t.status] || ""}`}
                data-testid={`hd-list-status-${t.id}`}>
          {["new","open","in_progress","waiting","resolved","closed","reopened"].map((s) => (
            <option key={s} value={s}>{s.replace("_", " ")}</option>
          ))}
        </select>
      ) },
    { key: "priority", label: "Priority", width: 110, sortable: true, groupable: true, filterType: "select",
      filterOptions: ["urgent","high","normal","low"].map((v) => ({ value: v, label: v })),
      render: (t) => (
        <select onClick={(e) => e.stopPropagation()}
                value={t.priority || "normal"}
                onChange={(e) => inlinePatch(t, "priority", e.target.value)}
                className={`text-[10px] uppercase font-semibold px-1 py-0.5 border bg-background ${PRIORITY_TONES[t.priority] || PRIORITY_TONES.normal}`}
                data-testid={`hd-list-priority-${t.id}`}>
          {["urgent","high","normal","low"].map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      ) },
    { key: "assignee_name", label: "Assignee", width: 160, sortable: true, groupable: true, filterType: "text",
      accessor: (t) => t.assignee_name || t.assignee_user_id || "",
      render: (t) => (
        <select onClick={(e) => e.stopPropagation()}
                value={t.assignee_user_id || ""}
                onChange={(e) => inlinePatch(t, "assignee_user_id", e.target.value)}
                className="text-xs px-1 py-0.5 border border-border bg-background w-full"
                data-testid={`hd-list-assignee-${t.id}`}>
          <option value="">— Unassigned —</option>
          {members.map((m) => (
            <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>
          ))}
        </select>
      ) },
    { key: "queue_name", label: "Queue", width: 140, sortable: true, groupable: true, filterType: "text",
      render: (t) => (
        <select onClick={(e) => e.stopPropagation()}
                value={t.queue_id || ""}
                onChange={(e) => inlinePatch(t, "queue_id", e.target.value)}
                className="text-xs px-1 py-0.5 border border-border bg-background w-full"
                data-testid={`hd-list-queue-${t.id}`}>
          <option value="">— None —</option>
          {queues.map((q) => (
            <option key={q.id} value={q.id}>{q.name}</option>
          ))}
        </select>
      ) },
    { key: "category_name", label: "Category", width: 140, sortable: true, groupable: true, filterType: "text",
      render: (t) => <span className="text-xs">{t.category_name || <span className="text-muted-foreground">—</span>}</span> },
    { key: "brand_name", label: "Brand", width: 130, sortable: true, groupable: true, filterType: "text",
      render: (t) => <span className="text-xs">{t.brand_name || <span className="text-muted-foreground">—</span>}</span> },
    { key: "client_name", label: "Client", width: 150, sortable: true, groupable: true, filterType: "text",
      render: (t) => <span className="text-xs">{t.client_name || <span className="text-muted-foreground">—</span>}</span> },
    { key: "project_name", label: "Project", width: 150, sortable: true, groupable: true, filterType: "text",
      render: (t) => <span className="text-xs">{t.project_name || <span className="text-muted-foreground">—</span>}</span> },
    { key: "requester_email", label: "Requester", width: 200, sortable: true, filterType: "text", visible: false,
      render: (t) => <span className="text-xs text-muted-foreground truncate">{t.requester_email || "—"}</span> },
    { key: "tags", label: "Tags", width: 160, filterType: "text", visible: false,
      accessor: (t) => (t.tags || []).join(", "),
      render: (t) => (t.tags || []).length
        ? <div className="flex gap-1 flex-wrap">{t.tags.slice(0, 3).map((tg) => <span key={tg} className="chip text-[10px]">{tg}</span>)}</div>
        : <span className="text-muted-foreground text-xs">—</span> },
    { key: "sla_resolve_due_at", label: "SLA due", width: 130, sortable: true, filterType: "date-range",
      render: (t) => t.sla_resolve_due_at && !t.resolved_at
        ? <SlaClock due={t.sla_resolve_due_at} />
        : <span className="text-muted-foreground text-xs">—</span> },
    { key: "created_at", label: "Created", width: 130, sortable: true, align: "right", filterType: "date-range",
      render: (t) => <span className="text-xs text-muted-foreground">{fmtRelative(t.created_at)}</span> },
    { key: "resolved_at", label: "Resolved", width: 130, sortable: true, align: "right", filterType: "date-range", visible: false,
      render: (t) => t.resolved_at ? <span className="text-xs text-emerald-600">{fmtRelative(t.resolved_at)}</span> : <span className="text-muted-foreground">—</span> },
    { key: "quick_actions", label: "", width: 60, align: "right",
      render: (t) => (
        <button type="button"
                onClick={(e) => { e.stopPropagation(); setPreview(t.id); }}
                className="p-1 hover:bg-muted rounded-none"
                title="Quick preview — discussion + changelog"
                data-testid={`hd-ticket-preview-${t.id}`}>
          <Eye className="w-3.5 h-3.5" />
        </button>
      ) },
  ];
  return (
    <>
      <AdvancedTable
        tableKey="helpdesk-tickets"
        title="Tickets"
        rows={tickets}
        columns={columns}
        emptyIcon={Ticket}
        emptyTitle="No tickets in this view"
        emptyDescription="Once tickets come in through the widget or email, they'll show up here."
        selectable={{
          selectedIds: selected, onSelectionChange: setSelected,
          idKey: "id",
        }}
        onRowClick={(t) => nav(`/app/helpdesk/tickets/${t.id}`)}
        data-testid="hd-tickets-smart-table"
      />
      {preview && (
        <TicketQuickPreview tid={preview} onClose={() => setPreview(null)} />
      )}
    </>
  );
}

// Iter 253x · Right-hand drawer showing last 5 comments + last 5 activity
// rows so agents can triage without navigating away from the list.
function TicketQuickPreview({ tid, onClose }) {
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [t, c, a] = await Promise.all([
          api.get(`/helpdesk/tickets/${tid}`),
          api.get(`/threads/ticket/${tid}/comments`).catch(() => ({ data: [] })),
          api.get(`/helpdesk/tickets/${tid}/activity`).catch(() => ({ data: [] })),
        ]);
        setTicket(t.data);
        setComments((c.data || []).slice(-5));
        setActivity((a.data || []).slice(-8));
        } catch { /* preview drawer is best-effort */ }
    })();
  }, [tid]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-stretch justify-end" onClick={onClose}>
      <div className="w-[420px] bg-background border-l border-border overflow-auto"
           onClick={(e) => e.stopPropagation()}
           data-testid="hd-ticket-preview-drawer">
        <div className="p-3 border-b border-border flex items-center justify-between sticky top-0 bg-background z-10">
          <div className="min-w-0">
            <div className="text-[10px] font-mono text-muted-foreground">{ticket?.ticket_number || "…"}</div>
            <div className="font-semibold text-sm truncate">{ticket?.subject || "Loading…"}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 space-y-3 text-xs">
          <div>
            <div className="label-eyebrow inline-flex items-center gap-1 mb-1"><MessageSquare className="w-3 h-3" /> Discussion</div>
            {comments.length === 0 && <div className="text-muted-foreground italic">No comments yet.</div>}
            {comments.map((c) => (
              c.kind === "system" ? (
                <div key={c.id} className="text-[11px] text-muted-foreground italic py-1"
                     data-testid={`hd-ticket-preview-sys-${c.id}`}>
                  <span className="text-foreground/70 font-medium not-italic">{c.author_name}</span> {c.body_text || c.body}
                </div>
              ) : (
                <div key={c.id} className="border-l-2 border-border pl-2 py-1"
                     data-testid={`hd-ticket-preview-comment-${c.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{c.author_name}</span>
                    <span className="text-[10px] text-muted-foreground">{fmtRelative(c.created_at)}</span>
                  </div>
                  <div className="text-[12px]" dangerouslySetInnerHTML={{ __html: c.body }} />
                </div>
              )
            ))}
          </div>
          <div>
            <div className="label-eyebrow inline-flex items-center gap-1 mb-1"><History className="w-3 h-3" /> Change log</div>
            {activity.length === 0 && <div className="text-muted-foreground italic">No activity yet.</div>}
            {activity.map((a) => (
              <div key={a.id} className="flex items-center gap-2 py-0.5 text-[11px]"
                   data-testid={`hd-ticket-preview-activity-${a.id}`}>
                <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
                <span className="font-medium text-foreground/70">{a.actor_name || "System"}</span>
                <span>{a.kind}</span>
                <span className="text-muted-foreground ml-auto">{fmtRelative(a.at)}</span>
              </div>
            ))}
          </div>
          <button onClick={() => { onClose(); window.location.href = `/app/helpdesk/tickets/${tid}`; }}
                  className="w-full h-8 bg-primary text-primary-foreground text-xs font-semibold rounded-none"
                  data-testid="hd-ticket-preview-open-full">
            Open full ticket →
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, icon: Icon, tone, testid }) {
  const tones = { danger: "border-red-500/40 bg-red-500/5", warn: "border-amber-500/40 bg-amber-500/5", ok: "border-emerald-500/40 bg-emerald-500/5" };
  return (
    <Card className={`rounded-none ${tone ? tones[tone] : ""}`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{label}</span>
          <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        </div>
        <div className="text-3xl font-bold mt-1.5 tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

function SlaClock({ due }) {
  const dueDate = new Date(due);
  const diff = dueDate - new Date();
  const hours = Math.round(diff / 3600000);
  const overdue = diff < 0;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-mono ${overdue ? "text-red-600 font-bold" : hours < 4 ? "text-amber-600 font-semibold" : ""}`}>
      <Clock className="w-3 h-3" /> {overdue ? `${Math.abs(hours)}h overdue` : `${hours}h`}
    </span>
  );
}

// ═════════════════════════ New Ticket Dialog ═════════════════════════

function NewTicketDialog({ wsId, queues, onClose, onCreated }) {
  const [form, setForm] = useState({
    queue_id: queues[0]?.id || "",
    subject: "",
    description_html: "",
    priority: "normal",
    severity: "medium",
    brand_id: "",
    client_id: "",
    project_id: "",
    assignee_user_id: "",
  });
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!wsId) return;
    let cancel = false;
    Promise.all([
      api.get(`/workspaces/${wsId}/helpdesk/categories`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${wsId}/brands`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${wsId}/clients`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${wsId}/projects`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${wsId}/members`).catch(() => ({ data: [] })),
    ]).then(([cat, b, c, p, m]) => {
      if (cancel) return;
      setCats(Array.isArray(cat.data) ? cat.data : (cat.data?.items || []));
      setBrands(Array.isArray(b.data) ? b.data : (b.data?.items || []));
      setClients(Array.isArray(c.data) ? c.data : (c.data?.items || []));
      setProjects(Array.isArray(p.data) ? p.data : (p.data?.items || []));
      setMembers(Array.isArray(m.data) ? m.data : (m.data?.items || []));
    });
    return () => { cancel = true; };
  }, [wsId]);
  // Filter projects by selected client/brand for quicker discovery.
  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      if (form.client_id && p.client_id && p.client_id !== form.client_id) return false;
      if (form.brand_id && p.brand_id && p.brand_id !== form.brand_id) return false;
      return true;
    });
  }, [projects, form.client_id, form.brand_id]);
  const submit = async () => {
    if (!form.queue_id) return toast.error("Pick a queue");
    if (!form.subject.trim()) return toast.error("Subject required");
    setBusy(true);
    try {
      const payload = {
        queue_id: form.queue_id,
        subject: form.subject,
        description_html: form.description_html || "",
        priority: form.priority,
        severity: form.severity,
        brand_id: form.brand_id || null,
        client_id: form.client_id || null,
        project_id: form.project_id || null,
        assignee_user_id: form.assignee_user_id || null,
        category_id: form.category_id || null,
      };
      const { data } = await api.post(`/workspaces/${wsId}/helpdesk/tickets`, payload);
      toast.success(`Ticket ${data.ticket_number} created`);
      onCreated(data.id);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-4xl w-[95vw] max-h-[92vh] overflow-y-auto rounded-none"
        data-testid="hd-new-ticket-dialog"
      >
        <DialogHeader>
          <DialogTitle>New ticket</DialogTitle>
          <DialogDescription>Route to a queue and add context — Client, Brand, Project & Assignee help agents resolve faster.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Row 1: Subject spans full width */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Subject <span className="text-red-500">*</span></label>
            <Input
              placeholder="Short summary of the issue"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="rounded-none"
              data-testid="new-ticket-subject"
            />
          </div>

          {/* Row 2: Queue · Category · Priority · Severity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Queue <span className="text-red-500">*</span></label>
              <select
                className="border border-border h-9 px-2 text-sm bg-background w-full rounded-none"
                value={form.queue_id}
                onChange={(e) => setForm({ ...form, queue_id: e.target.value })}
                data-testid="new-ticket-queue"
              >
                <option value="">— Select —</option>
                {queues.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Category (SLA)</label>
              <select
                className="border border-border h-9 px-2 text-sm bg-background w-full rounded-none"
                value={form.category_id || ""}
                onChange={(e) => setForm({ ...form, category_id: e.target.value || undefined })}
                data-testid="new-ticket-category"
              >
                <option value="">—</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Priority</label>
              <select
                className="border border-border h-9 px-2 text-sm bg-background w-full rounded-none"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                data-testid="new-ticket-priority"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Severity</label>
              <select
                className="border border-border h-9 px-2 text-sm bg-background w-full rounded-none"
                value={form.severity}
                onChange={(e) => setForm({ ...form, severity: e.target.value })}
                data-testid="new-ticket-severity"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          {/* Row 3: Client · Brand · Project · Assignee */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Client</label>
              <select
                className="border border-border h-9 px-2 text-sm bg-background w-full rounded-none"
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value, project_id: "" })}
                data-testid="new-ticket-client"
              >
                <option value="">—</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Brand</label>
              <select
                className="border border-border h-9 px-2 text-sm bg-background w-full rounded-none"
                value={form.brand_id}
                onChange={(e) => setForm({ ...form, brand_id: e.target.value, project_id: "" })}
                data-testid="new-ticket-brand"
              >
                <option value="">—</option>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Project</label>
              <select
                className="border border-border h-9 px-2 text-sm bg-background w-full rounded-none"
                value={form.project_id}
                onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                data-testid="new-ticket-project"
              >
                <option value="">—</option>
                {filteredProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Assignee</label>
              <select
                className="border border-border h-9 px-2 text-sm bg-background w-full rounded-none"
                value={form.assignee_user_id}
                onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })}
                data-testid="new-ticket-assignee"
              >
                <option value="">Auto (round-robin)</option>
                {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
              </select>
            </div>
          </div>

          {/* Row 4: Requester phone (optional) */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Requester phone (optional — enables click-to-call)</label>
            <Input
              placeholder="+91 98765 43210"
              value={form.requester_phone || ""}
              onChange={(e) => setForm({ ...form, requester_phone: e.target.value })}
              className="rounded-none"
              data-testid="new-ticket-phone"
            />
          </div>

          {/* Row 5: Description — SmartRichText */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Description</label>
            <SmartRichText
              value={form.description_html}
              onChange={(v) => setForm({ ...form, description_html: v })}
              placeholder="Describe the issue — paste screenshots, add links, use /commands for formatting…"
              data-testid="new-ticket-desc"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="new-ticket-save">
            {busy ? "Creating…" : "Create ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════ Ticket Worklogs (time tracking) ═════════════════════════

function TicketWorklogs({ tid }) {
  const [state, setState] = useState({ worklogs: [], total_minutes: 0, billable_minutes: 0 });
  const [form, setForm] = useState({ minutes: "", note: "", billable: true });
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const r = await api.get(`/helpdesk/tickets/${tid}/worklogs`);
      setState(r.data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, [tid]);
  const submit = async () => {
    const mins = parseInt(form.minutes, 10);
    if (!mins || mins <= 0) return toast.error("Minutes required");
    setBusy(true);
    try {
      await api.post(`/helpdesk/tickets/${tid}/worklogs`, {
        minutes: mins, note: form.note || null, billable: form.billable,
      });
      setForm({ minutes: "", note: "", billable: true });
      toast.success(`Logged ${mins}m`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to log time")); }
    finally { setBusy(false); }
  };
  const del = async (wid) => {
    if (!window.confirm("Delete this worklog?")) return;
    try {
      await api.delete(`/helpdesk/worklogs/${wid}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  const fmt = (m) => {
    const h = Math.floor(m / 60), rm = m % 60;
    return h ? `${h}h ${rm ? rm + "m" : ""}`.trim() : `${rm}m`;
  };
  return (
    <div data-testid="ticket-worklogs">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1.5">
        <Timer className="w-3 h-3" /> Time tracking
        {state.total_minutes > 0 && (
          <span className="ml-auto font-mono text-foreground">
            {fmt(state.total_minutes)} total · {fmt(state.billable_minutes)} billable
          </span>
        )}
      </div>
      <div className="border border-border p-2 space-y-2 bg-muted/20">
        <div className="flex gap-1.5 items-center">
          <input type="number" min="1" placeholder="Min" value={form.minutes}
                  onChange={(e) => setForm({ ...form, minutes: e.target.value })}
                  className="h-7 w-16 px-2 border border-border text-xs bg-background"
                  data-testid="ticket-worklog-mins" />
          <input type="text" placeholder="What did you do?" value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="h-7 flex-1 px-2 border border-border text-xs bg-background"
                  data-testid="ticket-worklog-note" />
          <label className="text-[10px] flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={form.billable}
                    onChange={(e) => setForm({ ...form, billable: e.target.checked })}
                    className="h-3 w-3"
                    data-testid="ticket-worklog-billable" />
            Bill
          </label>
          <Button size="sm" className="rounded-none h-7 text-xs px-2" onClick={submit} disabled={busy}
                  data-testid="ticket-worklog-add">
            <Plus className="w-3 h-3" />
          </Button>
        </div>
        {state.worklogs.length > 0 && (
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {state.worklogs.map((w) => (
              <div key={w.id} className="flex items-start gap-2 text-[11px] py-1 border-t border-border/50 first:border-t-0"
                    data-testid={`ticket-worklog-item-${w.id}`}>
                <span className="font-mono font-semibold min-w-[45px]">{fmt(w.minutes)}</span>
                <div className="flex-1 min-w-0">
                  <div className="truncate">{w.note || <span className="text-muted-foreground italic">no note</span>}</div>
                  <div className="text-muted-foreground text-[10px]">
                    {w.user_name} · {new Date(w.logged_at).toLocaleDateString()}
                    {!w.billable && <span className="ml-1 italic">· non-billable</span>}
                  </div>
                </div>
                <button onClick={() => del(w.id)} className="text-muted-foreground hover:text-red-600"
                        data-testid={`ticket-worklog-del-${w.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ═════════════════════════ Ticket Attachments ═════════════════════════

function TicketAttachments({ tid }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const r = await api.get(`/helpdesk/tickets/${tid}/attachments`);
      setRows(r.data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, [tid]);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) return toast.error("File too large (max 25 MB)");
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/attachments/ticket/${tid}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setBusy(false); }
  };
  const remove = async (aid) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await api.delete(`/attachments/${aid}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  const fmtBytes = (n) => n >= 1048576 ? `${(n / 1048576).toFixed(1)}MB` : n >= 1024 ? `${(n / 1024).toFixed(0)}KB` : `${n}B`;
  const backend = process.env.REACT_APP_BACKEND_URL;
  return (
    <div data-testid="ticket-attachments">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1.5">
        <Paperclip className="w-3 h-3" /> Attachments
        {rows.length > 0 && <span className="ml-auto normal-case text-[10px]">{rows.length}</span>}
      </div>
      <div className="border border-border p-2 bg-muted/20 space-y-1.5">
        <label className={`flex items-center justify-center gap-1 border-2 border-dashed border-border py-2 text-[11px] cursor-pointer hover:bg-muted/40 ${busy ? "opacity-50 pointer-events-none" : ""}`}
                data-testid="ticket-attachment-upload-label">
          <Upload className="w-3 h-3" /> {busy ? "Uploading…" : "Add attachment (max 25 MB)"}
          <input type="file" hidden onChange={(e) => upload(e.target.files?.[0])}
                  data-testid="ticket-attachment-input" />
        </label>
        {rows.map((a) => {
          const url = `${backend}/api/v1/attachments/file/${a.id}/download`;
          return (
            <div key={a.id} className="flex items-center gap-1.5 text-[11px] p-1 hover:bg-background"
                  data-testid={`ticket-attachment-${a.id}`}>
              <Paperclip className="w-3 h-3 text-muted-foreground shrink-0" />
              <a href={url} target="_blank" rel="noreferrer"
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      const r = await api.get(`/attachments/file/${a.id}/download`, { responseType: "blob" });
                      const objectUrl = window.URL.createObjectURL(r.data);
                      const link = document.createElement("a");
                      link.href = objectUrl; link.download = a.filename;
                      document.body.appendChild(link); link.click();
                      link.remove(); window.URL.revokeObjectURL(objectUrl);
                    } catch { toast.error("Download failed"); }
                  }}
                  className="flex-1 truncate underline decoration-dotted hover:text-foreground">
                {a.filename}
              </a>
              <span className="text-muted-foreground shrink-0">{fmtBytes(a.size)}</span>
              <button onClick={() => remove(a.id)} className="text-muted-foreground hover:text-red-600"
                       data-testid={`ticket-attachment-del-${a.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ═════════════════════════ Merge / Split dialogs ═════════════════════════

function MergeTicketDialog({ wsId, sourceTicket, onClose, onMerged }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!wsId) return;
    const params = new URLSearchParams();
    params.set("status", "new,open,in_progress,waiting,reopened,resolved");
    params.set("limit", "20");
    if (q) params.set("q", q);
    api.get(`/workspaces/${wsId}/helpdesk/tickets?${params.toString()}`)
      .then((r) => setResults((r.data || []).filter((x) => x.id !== sourceTicket.id)))
      .catch(() => setResults([]));
  }, [wsId, q, sourceTicket.id]);
  const doMerge = async (targetId) => {
    if (!window.confirm(`Merge ${sourceTicket.ticket_number} into this ticket? Comments will move over and ${sourceTicket.ticket_number} will be closed.`)) return;
    setBusy(true);
    try {
      await api.post(`/helpdesk/tickets/${sourceTicket.id}/merge`, {
        into_ticket_id: targetId, note: note || null,
      });
      toast.success("Merged");
      onMerged(targetId);
    } catch (e) { toast.error(apiErrorMessage(e, "Merge failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="hd-merge-dialog">
        <DialogHeader>
          <DialogTitle>Merge {sourceTicket.ticket_number} into…</DialogTitle>
          <DialogDescription>Comments & activity move to the target. Source ticket is closed with a link back.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input placeholder="Search subject or number…" value={q}
                  onChange={(e) => setQ(e.target.value)} data-testid="hd-merge-search" />
          <Textarea rows={2} placeholder="Optional merge note…" value={note}
                     onChange={(e) => setNote(e.target.value)} data-testid="hd-merge-note" />
          <div className="max-h-80 overflow-y-auto border border-border divide-y divide-border">
            {results.map((t) => (
              <button key={t.id}
                       onClick={() => doMerge(t.id)}
                       disabled={busy}
                       className="w-full text-left p-2 text-xs hover:bg-muted flex items-center gap-2"
                       data-testid={`hd-merge-target-${t.id}`}>
                <span className="font-mono text-muted-foreground">{t.ticket_number}</span>
                <span className="truncate flex-1 font-semibold">{t.subject}</span>
                <span className={`text-[9px] uppercase font-semibold px-1.5 border ${STATUS_TONES[t.status] || ""}`}>{t.status}</span>
              </button>
            ))}
            {results.length === 0 && <div className="p-4 text-center text-muted-foreground text-xs">No candidates</div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function SplitTicketDialog({ tid, sourceTicket, onClose, onSplit }) {
  const [form, setForm] = useState({ subject: "", description_html: "" });
  const [comments, setComments] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/threads/ticket/${tid}/comments`)
      .then((r) => setComments(r.data?.comments || r.data || []))
      .catch(() => setComments([]));
  }, [tid]);
  const submit = async () => {
    if (!form.subject.trim()) return toast.error("Subject required");
    setBusy(true);
    try {
      const { data } = await api.post(`/helpdesk/tickets/${tid}/split`, {
        subject: form.subject,
        description_html: form.description_html || `<em>Split from ${sourceTicket.ticket_number}</em>`,
        comment_ids: Array.from(selected),
      });
      toast.success(`Created ${data.ticket.ticket_number}`);
      onSplit(data.ticket.id);
    } catch (e) { toast.error(apiErrorMessage(e, "Split failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="hd-split-dialog">
        <DialogHeader>
          <DialogTitle>Split into new ticket</DialogTitle>
          <DialogDescription>Move selected comments into a new ticket that inherits requester & queue.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input placeholder="New ticket subject" value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  data-testid="hd-split-subject" />
          <Textarea rows={3} placeholder="Description (optional)…" value={form.description_html}
                     onChange={(e) => setForm({ ...form, description_html: e.target.value })}
                     data-testid="hd-split-desc" />
          {comments.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Move comments to new ticket</div>
              <div className="max-h-48 overflow-y-auto border border-border divide-y divide-border">
                {comments.map((c) => (
                  <label key={c.id} className="flex items-start gap-2 p-2 text-xs hover:bg-muted cursor-pointer"
                          data-testid={`hd-split-comment-${c.id}`}>
                    <input type="checkbox" checked={selected.has(c.id)}
                            onChange={() => {
                              const n = new Set(selected);
                              if (n.has(c.id)) n.delete(c.id); else n.add(c.id);
                              setSelected(n);
                            }}
                            className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] text-muted-foreground">{c.author_name || c.author_kind || "user"}</div>
                      <div className="line-clamp-2" dangerouslySetInnerHTML={{ __html: c.body || c.body_text || "" }} />
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="hd-split-submit">
            Split ticket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ═════════════════════════ Ticket Detail ═════════════════════════

export function HelpdeskTicketDetail() {
  const { tid } = useParams();
  const { activeWorkspaceId: wsId } = useAuth();
  const [data, setData] = useState(null);
  const [members, setMembers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [brands, setBrands] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showMerge, setShowMerge] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [t, m, q, b, c, p] = await Promise.all([
        api.get(`/helpdesk/tickets/${tid}`),
        wsId ? api.get(`/workspaces/${wsId}/members`) : Promise.resolve({ data: [] }),
        wsId ? api.get(`/workspaces/${wsId}/helpdesk/queues`) : Promise.resolve({ data: [] }),
        wsId ? api.get(`/workspaces/${wsId}/brands`) : Promise.resolve({ data: [] }),
        wsId ? api.get(`/workspaces/${wsId}/clients`, { params: { limit: 500 } }) : Promise.resolve({ data: [] }),
        wsId ? api.get(`/workspaces/${wsId}/projects`, { params: { limit: 500 } }) : Promise.resolve({ data: [] }),
      ]);
      setData(t.data); setMembers(m.data || []);
      setQueues(q.data || []);
      setBrands(Array.isArray(b.data) ? b.data : (b.data?.items || b.data?.brands || []));
      setClients(Array.isArray(c.data) ? c.data : (c.data?.items || c.data?.clients || []));
      setProjects(Array.isArray(p.data) ? p.data : (p.data?.items || p.data?.projects || []));
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
  };
  useEffect(() => { load(); }, [tid, wsId]);
  // Iter 253x/z-2 · Live wake tick — when a snoozed ticket's wake time
  // passes, refresh the doc so the amber "Snoozed" pill flips to the
  // "Snooze" button without a manual reload. Cheap 30s poll only while a
  // snooze is active.
  useEffect(() => {
    const snz = data?.ticket?.snoozed_until;
    if (!snz) return;
    const wakeMs = new Date(snz).getTime() - Date.now();
    if (wakeMs <= 0) { load(); return; }
    // Poll every 30s; also schedule an exact reload right when wake fires.
    const tick = setInterval(load, 30_000);
    const wake = setTimeout(load, Math.min(wakeMs + 1500, 3600_000));
    return () => { clearInterval(tick); clearTimeout(wake); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.ticket?.snoozed_until]);

  const setStatus = async (status) => {
    try {
      await api.post(`/helpdesk/tickets/${tid}/transition`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Transition failed")); }
  };
  const setAssignee = async (uid) => {
    try {
      await api.post(`/helpdesk/tickets/${tid}/assign`, { assignee_user_id: uid || null });
      toast.success("Assignee updated");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Assign failed")); }
  };
  // Iter 253r · Inline field patch.
  const patchTicket = async (fields) => {
    try {
      await api.patch(`/helpdesk/tickets/${tid}`, fields);
      toast.success("Ticket updated");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showSnooze, setShowSnooze] = useState(false);
  const submitCsat = async (rating) => {
    try {
      await api.post(`/helpdesk/tickets/${tid}/csat`, { rating });
      toast.success(`Thanks — ${rating}★`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "CSAT failed")); }
  };
  const copyMagicLink = async () => {
    try {
      const r = await api.post(`/helpdesk/tickets/${tid}/magic-link`);
      const url = `${window.location.origin}${r.data.url}`;
      const ok = await copyText(url);
      if (ok) toast.success("Magic link copied — send it to the client");
      else toast.info(`Magic link ready: ${url}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to mint link")); }
  };
  const runAiAnalyze = async () => {
    try {
      const r = await api.post(`/helpdesk/tickets/${tid}/ai-analyze`);
      toast.success("AI analysis updated");
      load();
      return r.data;
    } catch (e) { toast.error(apiErrorMessage(e, "AI analysis failed")); }
  };

  if (!data) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  const t = data.ticket;
  return (
    <div className="space-y-4" data-testid="helpdesk-ticket-detail-page">
      <button className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground" onClick={() => nav("/app/helpdesk")}>
        <ArrowLeft className="w-3 h-3" /> Inbox
      </button>
      <div className="border border-border p-4">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono text-muted-foreground">{t.ticket_number} · {t.source}</div>
            <h1 className="text-2xl font-bold mt-1" data-testid="ticket-subject">{t.subject}</h1>
            <div className="text-xs text-muted-foreground mt-1">
              From <b>{t.requester_name || t.requester_email || "internal"}</b> · Created {new Date(t.created_at).toLocaleString()}
              {t.reopen_count > 0 && <span className="ml-2 text-red-600 font-semibold">· Reopened {t.reopen_count}x</span>}
              {t.requester_phone && (
                <span className="ml-2 inline-flex items-center gap-1">· <span className="font-mono">{t.requester_phone}</span>
                  <CallButton phone={t.requester_phone} customer_name={t.requester_name}
                    entity_type="ticket" entity_id={t.id}
                    entity_name={t.ticket_number || t.subject}
                    className="rounded-none h-6 px-1.5" testid={`ticket-call-${t.id}`} />
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 border ${PRIORITY_TONES[t.priority] || PRIORITY_TONES.normal}`} data-testid="ticket-priority">{t.priority}</span>
              <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 border ${STATUS_TONES[t.status] || ""}`} data-testid="ticket-status">{(t.status || "").replace("_", " ")}</span>
            </div>
            {t.sla_resolve_due_at && !t.resolved_at && <SlaClock due={t.sla_resolve_due_at} />}
            <div className="flex gap-1.5 flex-wrap justify-end">
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={copyMagicLink}
                      data-testid="ticket-magic-link-btn">
                <Link2 className="w-3 h-3 mr-1" /> Client link
              </Button>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => setShowMerge(true)}
                      data-testid="ticket-merge-btn">
                <GitMerge className="w-3 h-3 mr-1" /> Merge
              </Button>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => setShowSplit(true)}
                      data-testid="ticket-split-btn">
                <Split className="w-3 h-3 mr-1" /> Split
              </Button>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => setShowCreateTask(true)}
                      data-testid="ticket-create-task-btn">
                <Plus className="w-3 h-3 mr-1" /> Create task
              </Button>
              {t.snoozed_until && new Date(t.snoozed_until) > new Date() ? (
                <Button size="sm" variant="outline" className="rounded-none h-7 text-xs border-amber-300 bg-amber-50 text-amber-800"
                        onClick={async () => {
                          try {
                            await api.delete(`/helpdesk/tickets/${tid}/snooze`);
                            toast.success("Woke up early");
                            load();
                          } catch (e) { toast.error(apiErrorMessage(e, "Wake failed")); }
                        }}
                        data-testid="ticket-unsnooze-btn">
                  <Clock className="w-3 h-3 mr-1" /> Snoozed till {new Date(t.snoozed_until).toLocaleString()} · Wake
                </Button>
              ) : (
                <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => setShowSnooze(true)}
                        data-testid="ticket-snooze-btn">
                  <Clock className="w-3 h-3 mr-1" /> Snooze
                </Button>
              )}
            </div>
          </div>
        </div>
        {/* Iter 253r · Inline editable fields — priority · severity · tags · subject · description */}
        <TicketEditPanel ticket={t} onPatch={patchTicket} />
        {/* Description rendered inside TicketEditPanel above */}

        {/* AI insights strip */}
        {(t.ai_sentiment || t.ai_summary || t.ai_tags?.length) && (
          <div className="mt-4 border-t border-border pt-3" data-testid="ticket-ai-panel">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
              <span>AI insights</span>
              {t.ai_analyzed_at && <span className="normal-case font-normal">· analyzed {new Date(t.ai_analyzed_at).toLocaleString()}</span>}
              <button className="ml-auto text-[10px] underline hover:text-foreground" onClick={runAiAnalyze}
                      data-testid="ticket-ai-reanalyze">Re-analyze</button>
            </div>
            {t.ai_summary && <div className="text-sm italic text-slate-700" data-testid="ticket-ai-summary">&ldquo;{t.ai_summary}&rdquo;</div>}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {t.ai_sentiment && (
                <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 border ${
                  t.ai_sentiment === "angry" ? "bg-red-100 border-red-200 text-red-700"
                  : t.ai_sentiment === "frustrated" ? "bg-amber-100 border-amber-200 text-amber-700"
                  : t.ai_sentiment === "positive" ? "bg-emerald-100 border-emerald-200 text-emerald-700"
                  : "bg-slate-100 border-slate-200 text-slate-600"
                }`} data-testid="ticket-ai-sentiment">
                  🎭 {t.ai_sentiment}
                </span>
              )}
              {(t.ai_tags || []).map((tag, i) => (
                <span key={i} className="text-[10px] uppercase font-semibold px-2 py-0.5 border bg-slate-50 border-slate-200 text-slate-600">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}
        {!t.ai_analyzed_at && (
          <div className="mt-3">
            <button className="text-[10px] underline text-muted-foreground hover:text-foreground"
                     onClick={runAiAnalyze} data-testid="ticket-ai-analyze-btn">
              Run AI analysis
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border p-3">
          <div className="text-[11px] uppercase tracking-widest font-semibold mb-2">Conversation</div>
          <CommentThread entityType="ticket" entityId={tid} testid={`ticket-thread-${tid}`}
                          // eslint-disable-next-line react/no-unstable-nested-components
                          extraToolbar={({ setDraft, draft }) => (
                            <TemplatePicker wsId={wsId} onPick={(body) => {
                              const rendered = body
                                .replace(/\{\{customer_name\}\}/g, t.requester_name || "there")
                                .replace(/\{\{ticket_number\}\}/g, t.ticket_number || "");
                              setDraft((draft || "") + rendered);
                            }} />
                          )} />
        </div>
        <div className="border border-border p-3 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Assignee</div>
            <select className="w-full h-9 border border-border px-2 text-sm bg-background" value={t.assignee_user_id || ""}
                    onChange={(e) => setAssignee(e.target.value)} data-testid="ticket-assignee-select">
              <option value="">— Unassigned —</option>
              {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
            </select>
          </div>
          <TicketRelations ticket={t} queues={queues} brands={brands} clients={clients} projects={projects} onPatched={load} />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Move to</div>
            <div className="grid grid-cols-2 gap-1.5">
              {["open", "in_progress", "waiting", "resolved", "closed", "reopened"].map((s) => (
                <button key={s} onClick={() => setStatus(s)}
                        className={`text-[11px] uppercase font-semibold py-1.5 border ${STATUS_TONES[s] || ""} hover:opacity-80 disabled:opacity-40`}
                        disabled={s === t.status}
                        data-testid={`ticket-status-btn-${s}`}>{s.replace("_", " ")}</button>
              ))}
            </div>
          </div>
          {(t.status === "resolved" || t.status === "closed") && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">CSAT</div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => submitCsat(n)}
                          className={`p-1 ${t.csat_rating >= n ? "text-amber-500" : "text-muted-foreground"} hover:text-amber-500`}
                          data-testid={`ticket-csat-${n}`}>
                    <Star className="w-4 h-4" fill={t.csat_rating >= n ? "currentColor" : "none"} />
                  </button>
                ))}
              </div>
              {t.csat_rating && <div className="text-[10px] text-muted-foreground mt-1">Rated {t.csat_rating}/5</div>}
            </div>
          )}
          {/* Activity timeline */}
          {data.activity.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Activity</div>
              <div className="space-y-1 text-[11px] max-h-64 overflow-y-auto">
                {data.activity.slice().reverse().map((a) => (
                  <div key={a.id} className="border-l-2 border-border pl-2 py-0.5">
                    <div className="font-medium">{a.kind.replace(/_/g, " ")}</div>
                    <div className="text-muted-foreground">{a.actor_name || "system"} · {new Date(a.created_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <TicketWorklogs tid={tid} />
          <TicketAttachments tid={tid} />
        </div>
      </div>

      {showMerge && (
        <MergeTicketDialog wsId={wsId} sourceTicket={t}
                            onClose={() => setShowMerge(false)}
                            onMerged={(targetId) => { setShowMerge(false); nav(`/app/helpdesk/tickets/${targetId}`); }} />
      )}
      {showSplit && (
        <SplitTicketDialog tid={tid} sourceTicket={t}
                            onClose={() => setShowSplit(false)}
                            onSplit={(newId) => { setShowSplit(false); nav(`/app/helpdesk/tickets/${newId}`); }} />
      )}
      {showCreateTask && (
        <CreateTaskFromTicketDialog tid={tid} ticket={t} wsId={wsId}
                                       onClose={() => setShowCreateTask(false)}
                                       onCreated={() => { setShowCreateTask(false); load(); }} />
      )}
      {showSnooze && (
        <SnoozeTicketDialog tid={tid}
                              onClose={() => setShowSnooze(false)}
                              onSnoozed={() => { setShowSnooze(false); load(); }} />
      )}
    </div>
  );
}


// ─────── Iter 253r · Ticket edit panel + Create task dialog + Screenshot ───────

function TicketEditPanel({ ticket, onPatch }) {
  const [editingDesc, setEditingDesc] = useState(false);
  const [desc, setDesc] = useState(ticket.description_html || "");
  useEffect(() => { setDesc(ticket.description_html || ""); }, [ticket.description_html]);
  const [tagsStr, setTagsStr] = useState((ticket.tags || []).join(", "));
  useEffect(() => { setTagsStr((ticket.tags || []).join(", ")); }, [ticket.tags]);

  const commitTags = () => {
    const arr = tagsStr.split(",").map((s) => s.trim()).filter(Boolean);
    if (JSON.stringify(arr) !== JSON.stringify(ticket.tags || [])) {
      onPatch({ tags: arr });
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-3 space-y-3" data-testid="ticket-edit-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Priority</div>
          <select className="h-8 px-2 border border-border text-xs bg-background w-full"
                  value={ticket.priority || "normal"}
                  onChange={(e) => onPatch({ priority: e.target.value })}
                  data-testid="ticket-edit-priority">
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Severity</div>
          <select className="h-8 px-2 border border-border text-xs bg-background w-full"
                  value={ticket.severity || "medium"}
                  onChange={(e) => onPatch({ severity: e.target.value })}
                  data-testid="ticket-edit-severity">
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>
        <div className="col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Tags (comma-separated)</div>
          <Input value={tagsStr}
                 onChange={(e) => setTagsStr(e.target.value)}
                 onBlur={commitTags}
                 onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
                 placeholder="e.g. billing, urgent, mobile"
                 className="h-8 rounded-none text-xs"
                 data-testid="ticket-edit-tags" />
          {(ticket.tags || []).length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {ticket.tags.map((tag, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 border border-border">{tag}</span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Description</div>
          {!editingDesc && (
            <button type="button" className="text-[10px] underline hover:text-foreground"
                    onClick={() => setEditingDesc(true)}
                    data-testid="ticket-edit-desc-btn">Edit</button>
          )}
        </div>
        {editingDesc ? (
          <>
            <SmartRichText value={desc} onChange={setDesc}
                            placeholder="Ticket description…"
                            data-testid="ticket-edit-desc-editor" />
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="ghost" className="rounded-none h-7 text-xs"
                      onClick={() => { setDesc(ticket.description_html || ""); setEditingDesc(false); }}
                      data-testid="ticket-edit-desc-cancel">Cancel</Button>
              <Button size="sm" className="rounded-none h-7 text-xs"
                      onClick={() => { onPatch({ description_html: desc }); setEditingDesc(false); }}
                      data-testid="ticket-edit-desc-save">Save</Button>
            </div>
          </>
        ) : ticket.description_html ? (
          <div className="text-sm prose max-w-none" dangerouslySetInnerHTML={{ __html: ticket.description_html }} />
        ) : (
          <div className="text-xs text-muted-foreground italic">No description. Click Edit to add one.</div>
        )}
      </div>
    </div>
  );
}


function CreateTaskFromTicketDialog({ tid, ticket, wsId, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: ticket.subject || "",
    description: "",
    priority: "medium",
    due_date: "",
    brand_id: ticket.brand_id || "",
    client_id: ticket.client_id || "",
    project_id: ticket.project_id || "",
    assignee_id: ticket.assignee_user_id || "",
    is_external: false,
  });
  const [brands, setBrands] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get(`/workspaces/${wsId}/brands`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${wsId}/clients`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${wsId}/projects`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${wsId}/members`).catch(() => ({ data: [] })),
    ]).then(([b, c, p, m]) => {
      if (cancel) return;
      setBrands(Array.isArray(b.data) ? b.data : (b.data?.items || []));
      setClients(Array.isArray(c.data) ? c.data : (c.data?.items || []));
      setProjects(Array.isArray(p.data) ? p.data : []);
      setMembers(Array.isArray(m.data) ? m.data : []);
    });
    return () => { cancel = true; };
  }, [wsId]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      await api.post(`/helpdesk/tickets/${tid}/create-task`, {
        title: form.title, description: form.description || null,
        priority: form.priority,
        due_date: form.due_date || null,
        brand_id: form.brand_id || null,
        client_id: form.client_id || null,
        project_id: form.project_id || null,
        assignee_id: form.assignee_id || null,
        is_external: form.is_external,
      });
      toast.success("Task created + linked to ticket");
      onCreated();
    } catch (e) { toast.error(apiErrorMessage(e, "Create task failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg rounded-none" data-testid="ticket-create-task-dialog">
        <DialogHeader>
          <DialogTitle>Create task from ticket #{ticket.ticket_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest">Title</label>
            <Input value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                   className="rounded-none" data-testid="ticket-task-title" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest">Description (optional)</label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                      className="rounded-none" data-testid="ticket-task-description" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Priority</label>
              <select value={form.priority} onChange={(e) => setForm((s) => ({ ...s, priority: e.target.value }))}
                      className="h-9 px-2 border border-border w-full bg-background text-sm"
                      data-testid="ticket-task-priority">
                <option value="low">Low</option><option value="medium">Medium</option>
                <option value="high">High</option><option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Due date</label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm((s) => ({ ...s, due_date: e.target.value }))}
                     className="h-9 rounded-none" data-testid="ticket-task-due" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Assignee</label>
              <select value={form.assignee_id} onChange={(e) => setForm((s) => ({ ...s, assignee_id: e.target.value }))}
                      className="h-9 px-2 border border-border w-full bg-background text-sm"
                      data-testid="ticket-task-assignee">
                <option value="">Unassigned</option>
                {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Brand</label>
              <select value={form.brand_id} onChange={(e) => setForm((s) => ({ ...s, brand_id: e.target.value }))}
                      className="h-9 px-2 border border-border w-full bg-background text-sm"
                      data-testid="ticket-task-brand">
                <option value="">—</option>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Client</label>
              <select value={form.client_id} onChange={(e) => setForm((s) => ({ ...s, client_id: e.target.value }))}
                      className="h-9 px-2 border border-border w-full bg-background text-sm"
                      data-testid="ticket-task-client">
                <option value="">—</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Project</label>
              <select value={form.project_id} onChange={(e) => setForm((s) => ({ ...s, project_id: e.target.value }))}
                      className="h-9 px-2 border border-border w-full bg-background text-sm"
                      data-testid="ticket-task-project">
                <option value="">—</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.is_external} onChange={(e) => setForm((s) => ({ ...s, is_external: e.target.checked }))}
                   data-testid="ticket-task-external" />
            External task (client can see)
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="ticket-task-submit">
            {busy ? "Creating…" : "Create task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Iter 253z · Snooze a ticket until a chosen date/time.
function SnoozeTicketDialog({ tid, onClose, onSnoozed }) {
  // Default: tomorrow 9am local
  const defaultWake = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    // format for datetime-local input (YYYY-MM-DDTHH:mm)
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();
  const [wake, setWake] = useState(defaultWake);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!wake) { toast.error("Pick a wake-up date & time"); return; }
    const iso = new Date(wake).toISOString();
    setBusy(true);
    try {
      await api.post(`/helpdesk/tickets/${tid}/snooze`, {
        snoozed_until: iso, reason: reason || null,
      });
      toast.success("Ticket snoozed");
      onSnoozed();
    } catch (e) { toast.error(apiErrorMessage(e, "Snooze failed")); }
    finally { setBusy(false); }
  };
  const quick = (hoursOrDate) => {
    const d = new Date();
    if (typeof hoursOrDate === "number") {
      d.setHours(d.getHours() + hoursOrDate);
    } else {
      // "monday9am" / "tomorrow9am"
      if (hoursOrDate === "tomorrow9am") {
        d.setDate(d.getDate() + 1);
        d.setHours(9, 0, 0, 0);
      } else if (hoursOrDate === "monday9am") {
        const days = (8 - d.getDay()) % 7 || 7;
        d.setDate(d.getDate() + days);
        d.setHours(9, 0, 0, 0);
      } else if (hoursOrDate === "nextweek") {
        d.setDate(d.getDate() + 7);
        d.setHours(9, 0, 0, 0);
      }
    }
    const pad = (n) => String(n).padStart(2, "0");
    setWake(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md rounded-none" data-testid="ticket-snooze-dialog">
        <DialogHeader>
          <DialogTitle>Snooze this ticket</DialogTitle>
          <DialogDescription>
            Hide from your active inbox until the wake-up time. SLA escalations pause too.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {[["1h", 1], ["4h", 4], ["Tomorrow 9am", "tomorrow9am"], ["Monday 9am", "monday9am"], ["Next week", "nextweek"]].map(([lbl, v]) => (
              <button key={lbl} type="button" onClick={() => quick(v)}
                      className="h-7 px-2 text-[11px] uppercase tracking-widest border border-border hover:bg-muted"
                      data-testid={`snooze-quick-${String(v).replace(/[^a-z0-9]/gi, "")}`}>
                {lbl}
              </button>
            ))}
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground tracking-widest">Wake up at</label>
            <Input type="datetime-local" value={wake} onChange={(e) => setWake(e.target.value)}
                    className="h-9 rounded-none" data-testid="snooze-wake-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground tracking-widest">Reason (optional)</label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Waiting on customer info…"
                    className="h-9 rounded-none" data-testid="snooze-reason-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="snooze-submit-btn">
            {busy ? "Snoozing…" : "Snooze"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ═════════════════════════ Settings ═════════════════════════

export function HelpdeskSettings() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [tab, setTab] = useState("queues");
  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="helpdesk-settings-page">
      <Link to="/app/helpdesk" className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground">
        <ArrowLeft className="w-3 h-3" /> Inbox
      </Link>
      <PageHeader title="Helpdesk Settings" subtitle="Queues, categories, SLAs, widgets & templates." icon={Settings2} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none">
          <TabsTrigger value="queues" className="rounded-none" data-testid="hd-set-tab-queues">Queues</TabsTrigger>
          <TabsTrigger value="categories" className="rounded-none" data-testid="hd-set-tab-categories">Categories &amp; SLA</TabsTrigger>
          <TabsTrigger value="widgets" className="rounded-none" data-testid="hd-set-tab-widgets">Widgets</TabsTrigger>
          <TabsTrigger value="ai" className="rounded-none" data-testid="hd-set-tab-ai">AI &amp; KB</TabsTrigger>
          <TabsTrigger value="templates" className="rounded-none" data-testid="hd-set-tab-templates">Templates</TabsTrigger>
        </TabsList>
        <TabsContent value="queues" className="pt-3"><QueuesTab wsId={wsId} /></TabsContent>
        <TabsContent value="categories" className="pt-3"><CategoriesTab wsId={wsId} /></TabsContent>
        <TabsContent value="widgets" className="pt-3"><WidgetsTab wsId={wsId} /></TabsContent>
        <TabsContent value="ai" className="pt-3"><AiAndKbTab wsId={wsId} /></TabsContent>
        <TabsContent value="templates" className="pt-3"><TemplatesTab wsId={wsId} /></TabsContent>
      </Tabs>
    </div>
  );
}

/** Iter 253y · Reusable searchable multi-select for agents. Wraps a chip
 * strip + inline search + dropdown so admins never have to Cmd-click a
 * native <select multiple>.  */
function AgentMultiSelect({ members, value, onChange, testidPrefix }) {
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const selectedIds = value || [];
  const selectedRows = members.filter((m) => selectedIds.includes(m.user_id));
  const remaining = members.filter((m) => !selectedIds.includes(m.user_id));
  const ql = q.trim().toLowerCase();
  const matches = ql
    ? remaining.filter((m) =>
        (m.name || "").toLowerCase().includes(ql) ||
        (m.email || "").toLowerCase().includes(ql))
    : remaining;
  const add = (uid) => onChange([...selectedIds, uid]);
  const remove = (uid) => onChange(selectedIds.filter((x) => x !== uid));
  return (
    <div className="relative" data-testid={testidPrefix}>
      <div className="min-h-[36px] border border-border bg-background px-1 py-1 flex flex-wrap gap-1 items-center">
        {selectedRows.map((m) => (
          <span key={m.user_id} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-[11px] px-1.5 py-0.5"
                data-testid={`${testidPrefix}-chip-${m.user_id}`}>
            {m.name || m.email}
            <button type="button" onClick={() => remove(m.user_id)}
                     className="hover:text-rose-600"
                     data-testid={`${testidPrefix}-remove-${m.user_id}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
               onFocus={() => setOpen(true)}
               onBlur={() => setTimeout(() => setOpen(false), 120)}
               placeholder={selectedRows.length ? "Add another…" : "Search agents…"}
               className="flex-1 min-w-[120px] text-xs h-6 px-1 outline-none bg-transparent"
               data-testid={`${testidPrefix}-input`} />
      </div>
      {open && matches.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 border border-border bg-background shadow-md max-h-52 overflow-auto"
             data-testid={`${testidPrefix}-popover`}>
          {matches.map((m) => (
            <button key={m.user_id} type="button"
                    onMouseDown={(e) => { e.preventDefault(); add(m.user_id); setQ(""); }}
                    className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted"
                    data-testid={`${testidPrefix}-opt-${m.user_id}`}>
              <span className="font-medium">{m.name || m.email}</span>
              {m.email && m.name && <span className="text-muted-foreground ml-2">{m.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function QueuesTab({ wsId }) {
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [manageAgentsQ, setManageAgentsQ] = useState(null);   // queue being edited
  const [form, setForm] = useState({ name: "", agent_ids: [], business_hours: "24x7" });
  const load = async () => {
    if (!wsId) return;
    const [q, m] = await Promise.all([
      api.get(`/workspaces/${wsId}/helpdesk/queues`, { params: { include_archived: true } }),
      api.get(`/workspaces/${wsId}/members`).catch(() => ({ data: [] })),
    ]);
    setRows(q.data); setMembers(m.data || []);
  };
  useEffect(() => { load(); }, [wsId]);
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Queue name required"); return; }
    try {
      await api.post(`/workspaces/${wsId}/helpdesk/queues`, form);
      toast.success("Queue created");
      setForm({ name: "", agent_ids: [], business_hours: "24x7" });
      setShowAdd(false); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
  };
  const startEdit = (q) => { setEditingId(q.id); setEditName(q.name); };
  const cancelEdit = () => { setEditingId(null); setEditName(""); };
  const saveRename = async (q) => {
    const nm = editName.trim();
    if (!nm) { toast.error("Name cannot be empty"); return; }
    if (nm === q.name) { cancelEdit(); return; }
    setSavingId(q.id);
    try {
      await api.patch(`/helpdesk/queues/${q.id}`, { name: nm });
      toast.success("Queue renamed");
      cancelEdit(); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Rename failed")); }
    finally { setSavingId(null); }
  };
  const toggleArchive = async (q) => {
    setSavingId(q.id);
    try {
      await api.patch(`/helpdesk/queues/${q.id}`, { is_active: !(q.is_active !== false) });
      toast.success(q.is_active !== false ? "Queue archived" : "Queue restored");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Archive failed")); }
    finally { setSavingId(null); }
  };
  const saveAgents = async (queue, agentIds, businessHours) => {
    setSavingId(queue.id);
    try {
      await api.patch(`/helpdesk/queues/${queue.id}`,
                            { agent_ids: agentIds, business_hours: businessHours });
      toast.success("Queue updated");
      setManageAgentsQ(null); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSavingId(null); }
  };
  const move = async (idx, dir) => {
    const visible = rows.filter((r) => showArchived || r.is_active !== false);
    const target = idx + dir;
    if (target < 0 || target >= visible.length) return;
    const ids = visible.map((r) => r.id);
    [ids[idx], ids[target]] = [ids[target], ids[idx]];
    const archivedTail = rows.filter((r) => r.is_active === false).map((r) => r.id);
    const merged = showArchived ? ids : [...ids, ...archivedTail];
    try {
      await api.post(`/workspaces/${wsId}/helpdesk/queues/reorder`, { ordered_queue_ids: merged });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Reorder failed")); }
  };
  const visible = rows.filter((r) => showArchived || r.is_active !== false);
  const archivedCt = rows.filter((r) => r.is_active === false).length;
  return (
    <div className="space-y-3" data-testid="hd-queues-tab">
      <div className="flex justify-between items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
          {visible.length} queue(s){archivedCt > 0 && !showArchived ? ` · ${archivedCt} archived` : ""}
        </span>
        <div className="flex items-center gap-2">
          {archivedCt > 0 && (
            <label className="inline-flex items-center gap-1 text-xs cursor-pointer" data-testid="hd-toggle-archived">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Show archived
            </label>
          )}
          <Button size="sm" className="rounded-none h-8" onClick={() => setShowAdd((v) => !v)} data-testid="hd-add-queue-btn">{showAdd ? "Cancel" : "+ New queue"}</Button>
        </div>
      </div>
      {showAdd && (
        <div className="border border-border p-3 bg-muted/30 space-y-3" data-testid="hd-queue-form">
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Queue name</Label>
            <Input placeholder="e.g. Billing Support" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className="rounded-none" data-testid="hd-queue-name" />
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Agents ({form.agent_ids.length})</Label>
            <AgentMultiSelect members={members} value={form.agent_ids}
                                  onChange={(ids) => setForm({ ...form, agent_ids: ids })}
                                  testidPrefix="hd-queue-agents" />
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Business hours</Label>
            <select className="border border-border h-9 px-2 text-sm bg-background w-full max-w-xs" value={form.business_hours}
                    onChange={(e) => setForm({ ...form, business_hours: e.target.value })} data-testid="hd-queue-hours">
              <option value="24x7">24×7 always-on</option>
              <option value="9x5">9 AM–5 PM, Mon–Fri</option>
            </select>
          </div>
          <div className="flex justify-end">
            <Button size="sm" className="rounded-none" onClick={submit} data-testid="hd-queue-save">Save queue</Button>
          </div>
        </div>
      )}
      <div className="border border-border divide-y divide-border">
        {visible.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No queues yet.</div> : visible.map((q, idx) => {
          const archived = q.is_active === false;
          const isEditing = editingId === q.id;
          const busy = savingId === q.id;
          return (
            <div key={q.id} className={`px-3 py-2 flex items-center gap-2 text-sm ${archived ? "opacity-60 bg-muted/30" : ""}`} data-testid={`hd-queue-row-${q.id}`}>
              <div className="flex flex-col gap-0.5 mr-1">
                <button type="button" className="p-0.5 hover:bg-muted disabled:opacity-30" disabled={idx === 0 || busy}
                        onClick={() => move(idx, -1)} title="Move up" data-testid={`hd-queue-up-${q.id}`}>
                  <ArrowUp className="w-3 h-3" />
                </button>
                <button type="button" className="p-0.5 hover:bg-muted disabled:opacity-30" disabled={idx === visible.length - 1 || busy}
                        onClick={() => move(idx, 1)} title="Move down" data-testid={`hd-queue-down-${q.id}`}>
                  <ArrowDown className="w-3 h-3" />
                </button>
              </div>
              <div className="flex-1 min-w-0">
                {isEditing ? (
                  <div className="flex items-center gap-1">
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                           className="h-7 text-sm rounded-none" autoFocus
                           onKeyDown={(e) => { if (e.key === "Enter") saveRename(q); if (e.key === "Escape") cancelEdit(); }}
                           data-testid={`hd-queue-rename-input-${q.id}`} />
                    <Button size="sm" className="rounded-none h-7 px-2" onClick={() => saveRename(q)} disabled={busy}
                            data-testid={`hd-queue-rename-save-${q.id}`}>
                      <CheckIcon className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={cancelEdit}
                            data-testid={`hd-queue-rename-cancel-${q.id}`}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold truncate" data-testid={`hd-queue-name-${q.id}`}>{q.name}</span>
                    {archived && <span className="chip text-[10px] bg-muted text-muted-foreground" data-testid={`hd-queue-archived-${q.id}`}>Archived</span>}
                    <span className="text-[10px] text-muted-foreground">
                      {q.agent_ids?.length || 0} agent{(q.agent_ids?.length || 0) === 1 ? "" : "s"} · {q.business_hours}
                    </span>
                  </div>
                )}
              </div>
              {!isEditing && (
                <div className="flex items-center gap-1">
                  <button type="button"
                           className="p-1 hover:bg-muted rounded-none inline-flex items-center gap-1 text-[11px]"
                           onClick={() => setManageAgentsQ(q)} title="Manage agents"
                           data-testid={`hd-queue-agents-btn-${q.id}`} disabled={busy}>
                    <Users className="w-3.5 h-3.5" /> Agents
                  </button>
                  <button type="button" className="p-1 hover:bg-muted rounded-none" onClick={() => startEdit(q)} title="Rename"
                          data-testid={`hd-queue-edit-${q.id}`} disabled={busy}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" className="p-1 hover:bg-muted rounded-none" onClick={() => toggleArchive(q)}
                          title={archived ? "Restore" : "Archive"} disabled={busy}
                          data-testid={`hd-queue-archive-${q.id}`}>
                    {archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {manageAgentsQ && (
        <ManageQueueAgentsDialog queue={manageAgentsQ} members={members}
                                        onClose={() => setManageAgentsQ(null)}
                                        onSave={saveAgents} />
      )}
    </div>
  );
}

function ManageQueueAgentsDialog({ queue, members, onClose, onSave }) {
  const [agents, setAgents] = React.useState(queue.agent_ids || []);
  const [businessHours, setBusinessHours] = React.useState(queue.business_hours || "24x7");
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg rounded-none" data-testid="hd-queue-agents-dialog">
        <DialogHeader>
          <DialogTitle>Manage “{queue.name}”</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Agents ({agents.length})</Label>
            <AgentMultiSelect members={members} value={agents}
                                  onChange={setAgents}
                                  testidPrefix="hd-queue-agents-manage" />
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Business hours</Label>
            <select className="border border-border h-9 px-2 text-sm bg-background w-full max-w-xs" value={businessHours}
                    onChange={(e) => setBusinessHours(e.target.value)}
                    data-testid="hd-queue-agents-hours">
              <option value="24x7">24×7 always-on</option>
              <option value="9x5">9 AM–5 PM, Mon–Fri</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={() => onSave(queue, agents, businessHours)}
                  data-testid="hd-queue-agents-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoriesTab({ wsId }) {
  const [rows, setRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", sla_first_response_mins: 60, sla_resolve_mins: 1440, default_priority: "normal", sla_by_priority: {} });
  const load = async () => { if (wsId) { const r = await api.get(`/workspaces/${wsId}/helpdesk/categories`); setRows(r.data); } };
  useEffect(() => { load(); }, [wsId]);
  const resetForm = () => setForm({ name: "", sla_first_response_mins: 60, sla_resolve_mins: 1440, default_priority: "normal", sla_by_priority: {} });
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Category name required"); return; }
    try {
      await api.post(`/workspaces/${wsId}/helpdesk/categories`, form);
      toast.success("Category created");
      resetForm();
      setShowAdd(false); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
  };
  const saveEdit = async (cat, patch) => {
    try {
      await api.patch(`/helpdesk/categories/${cat.id}`, patch);
      toast.success("Category updated");
      setEditingId(null); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  const destroy = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Existing tickets will keep their category link.`)) return;
    try {
      await api.delete(`/helpdesk/categories/${cat.id}`);
      toast.success("Category deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  return (
    <div className="space-y-3" data-testid="hd-categories-tab">
      <div className="flex justify-between items-center">
        <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
          {rows.length} categor{rows.length === 1 ? "y" : "ies"}
        </span>
        <Button size="sm" className="rounded-none h-8" onClick={() => setShowAdd((v) => !v)} data-testid="hd-add-cat-btn">{showAdd ? "Cancel" : "+ New category"}</Button>
      </div>
      {showAdd && (
        <CategoryForm form={form} setForm={setForm} onSave={submit} onCancel={() => { setShowAdd(false); resetForm(); }} testKey="hd-cat" />
      )}
      <div className="border border-border divide-y divide-border">
        {rows.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No categories yet — SLAs are only computed when a ticket has a category.</div> : rows.map((c) => (
          editingId === c.id ? (
            <div key={c.id} className="p-2 bg-muted/10">
              <CategoryForm form={c} setForm={() => {}} onSave={(patch) => saveEdit(c, patch)} onCancel={() => setEditingId(null)} testKey={`hd-cat-edit-${c.id}`} isEdit />
            </div>
          ) : (
            <div key={c.id} className="px-3 py-2 flex items-center gap-2 text-sm" data-testid={`hd-cat-row-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold truncate">{c.name}</span>
                  <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 border ${PRIORITY_TONES[c.default_priority] || PRIORITY_TONES.normal}`}>{c.default_priority}</span>
                  <span className="text-[10px] text-muted-foreground">
                    default: {c.sla_first_response_mins}m first / {c.sla_resolve_mins}m resolve
                  </span>
                </div>
                {c.sla_by_priority && Object.keys(c.sla_by_priority).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1" data-testid={`hd-cat-sla-summary-${c.id}`}>
                    {["urgent","high","normal","low"].filter((p) => c.sla_by_priority?.[p]).map((p) => {
                      const o = c.sla_by_priority[p] || {};
                      const first = o.first_response_mins || c.sla_first_response_mins;
                      const res = o.resolve_mins || c.sla_resolve_mins;
                      return (
                        <span key={p} className={`text-[10px] px-1.5 py-0.5 border ${PRIORITY_TONES[p]}`}
                              data-testid={`hd-cat-sla-tier-${c.id}-${p}`}>
                          {p}: {first}m / {res}m
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              <button type="button" className="p-1 hover:bg-muted"
                       onClick={() => setEditingId(c.id)} title="Edit"
                       data-testid={`hd-cat-edit-${c.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button type="button" className="p-1 hover:bg-rose-100 text-rose-600"
                       onClick={() => destroy(c)} title="Delete"
                       data-testid={`hd-cat-delete-${c.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

/** Iter 253y · Category editor with priority-tier SLA matrix. Used for both
 * create + inline edit. */
function CategoryForm({ form, setForm, onSave, onCancel, testKey, isEdit = false }) {
  const [local, setLocal] = React.useState(form);
  React.useEffect(() => { setLocal(form); }, [form?.id]);
  const set = (k, v) => setLocal((s) => ({ ...s, [k]: v }));
  const setTier = (p, k, v) => {
    const map = { ...(local.sla_by_priority || {}) };
    map[p] = { ...(map[p] || {}), [k]: v ? Number(v) : undefined };
    // Remove empty entries so the doc stays clean.
    if (!map[p].first_response_mins && !map[p].resolve_mins) delete map[p];
    set("sla_by_priority", map);
  };
  const doSave = () => {
    const payload = { ...local };
    if (isEdit) onSave(payload); else { onSave(payload); }
  };
  return (
    <div className="border border-border p-3 bg-muted/30 space-y-3" data-testid={`${testKey}-form`}>
      <div className="grid grid-cols-[2fr,1fr] gap-2">
        <div>
          <Label className="label-eyebrow mb-1 inline-block">Name</Label>
          <Input placeholder="e.g. Billing / Refunds" value={local.name || ""}
                 onChange={(e) => set("name", e.target.value)} className="rounded-none"
                 data-testid={`${testKey}-name`} />
        </div>
        <div>
          <Label className="label-eyebrow mb-1 inline-block">Default priority</Label>
          <select className="w-full border border-border h-9 px-2 text-sm bg-background" value={local.default_priority || "normal"}
                  onChange={(e) => set("default_priority", e.target.value)}
                  data-testid={`${testKey}-priority`}>
            <option value="low">Low</option><option value="normal">Normal</option>
            <option value="high">High</option><option value="urgent">Urgent</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="label-eyebrow mb-1 inline-block">Default first-response SLA (min)</Label>
          <Input type="number" value={local.sla_first_response_mins || 0}
                 onChange={(e) => set("sla_first_response_mins", parseInt(e.target.value || 0))}
                 className="rounded-none" data-testid={`${testKey}-sla-first`} />
        </div>
        <div>
          <Label className="label-eyebrow mb-1 inline-block">Default resolve SLA (min)</Label>
          <Input type="number" value={local.sla_resolve_mins || 0}
                 onChange={(e) => set("sla_resolve_mins", parseInt(e.target.value || 0))}
                 className="rounded-none" data-testid={`${testKey}-sla-resolve`} />
        </div>
      </div>
      <div className="border-t border-border pt-2">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
          Priority-tier overrides <span className="normal-case tracking-normal">(leave blank to use defaults above)</span>
        </div>
        <div className="border border-border" data-testid={`${testKey}-sla-matrix`}>
          <div className="grid grid-cols-[100px,1fr,1fr] text-[10px] uppercase tracking-widest text-muted-foreground bg-muted/40 border-b border-border">
            <div className="px-2 py-1">Priority</div>
            <div className="px-2 py-1 border-l border-border">First-response min</div>
            <div className="px-2 py-1 border-l border-border">Resolve min</div>
          </div>
          {["urgent","high","normal","low"].map((p) => {
            const o = (local.sla_by_priority || {})[p] || {};
            return (
              <div key={p} className="grid grid-cols-[100px,1fr,1fr] items-center border-t border-border">
                <div className="px-2 py-1">
                  <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 border ${PRIORITY_TONES[p]}`}>{p}</span>
                </div>
                <div className="px-1 border-l border-border">
                  <Input type="number" placeholder={String(local.sla_first_response_mins || "")}
                         value={o.first_response_mins || ""}
                         onChange={(e) => setTier(p, "first_response_mins", e.target.value)}
                         className="h-8 rounded-none border-0" data-testid={`${testKey}-tier-first-${p}`} />
                </div>
                <div className="px-1 border-l border-border">
                  <Input type="number" placeholder={String(local.sla_resolve_mins || "")}
                         value={o.resolve_mins || ""}
                         onChange={(e) => setTier(p, "resolve_mins", e.target.value)}
                         className="h-8 rounded-none border-0" data-testid={`${testKey}-tier-resolve-${p}`} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" className="rounded-none" onClick={onCancel}
                data-testid={`${testKey}-cancel`}>Cancel</Button>
        <Button size="sm" className="rounded-none" onClick={doSave}
                data-testid={`${testKey}-save`}>Save</Button>
      </div>
    </div>
  );
}

function WidgetsTab({ wsId }) {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null); // widget being edited (or 'new')
  const load = async () => {
    if (!wsId) return;
    const [w, c] = await Promise.all([
      api.get(`/workspaces/${wsId}/helpdesk/widgets`),
      api.get(`/workspaces/${wsId}/helpdesk/categories`),
    ]);
    setRows(w.data); setCats(c.data);
  };
  useEffect(() => { load(); }, [wsId]);

  const remove = async (w) => {
    if (!window.confirm(`Delete widget "${w.name}"? Installed snippets will stop working.`)) return;
    try {
      await api.delete(`/helpdesk/widgets/${w.id}`);
      toast.success("Widget deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  const regen = async (w) => {
    if (!window.confirm(`Regenerate snippet key for "${w.name}"? The old key will stop working immediately.`)) return;
    try {
      const r = await api.post(`/helpdesk/widgets/${w.id}/regenerate-key`);
      toast.success(`New key: ${r.data.snippet_key.slice(0, 6)}…`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Regenerate failed")); }
  };
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="space-y-3" data-testid="hd-widgets-tab">
      <div className="flex justify-between items-center">
        <span className="text-[11px] uppercase tracking-widest text-muted-foreground">{rows.length} widget(s)</span>
        <Button size="sm" className="rounded-none h-8" onClick={() => setEditing("new")}
                data-testid="hd-add-widget-btn">
          <Plus className="w-3 h-3 mr-1" /> New widget
        </Button>
      </div>

      {editing && (
        <WidgetEditor wsId={wsId} widget={editing === "new" ? null : editing}
                       categories={cats}
                       onDone={() => { setEditing(null); load(); }}
                       onCancel={() => setEditing(null)} />
      )}

      <div className="border border-border divide-y divide-border">
        {rows.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">No widgets yet. Create one to embed on your site.</div>
        ) : rows.map((w) => (
          <div key={w.id} className="px-3 py-3 space-y-2 text-sm" data-testid={`hd-widget-row-${w.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded" style={{ background: w.theme?.primary || "#0f172a" }} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{w.name}</div>
                <div className="text-[10px] text-muted-foreground font-mono">{w.snippet_key}</div>
              </div>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                      onClick={() => window.open(`/client/portal?ws=${wsId}`, "_blank")}
                      data-testid={`hd-widget-preview-${w.id}`}>
                <Eye className="w-3 h-3 mr-1" /> Preview portal
              </Button>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                      onClick={() => setEditing(w)}
                      data-testid={`hd-widget-edit-${w.id}`}>
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                      onClick={() => regen(w)}
                      data-testid={`hd-widget-regen-${w.id}`}>
                <RefreshCw className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs text-red-600"
                      onClick={() => remove(w)}
                      data-testid={`hd-widget-delete-${w.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <SnippetBox
                label="Embed snippet"
                code={`<script src="${backendUrl}/widget/acquilix.js" data-key="${w.snippet_key}" async></script>`}
                testid={`hd-widget-snippet-${w.id}`}
              />
              <SnippetBox
                label="Portal link"
                code={`${window.location.origin}/client/portal?ws=${wsId}`}
                testid={`hd-widget-portal-link-${w.id}`}
              />
              <SnippetBox
                label="Public help center (KB browse)"
                code={`${window.location.origin}/help/${w.snippet_key}`}
                testid={`hd-widget-kb-link-${w.id}`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SnippetBox({ label, code, testid }) {
  const copy = async () => {
    const ok = await copyText(code);
    if (ok) toast.success("Copied");
    else toast.error("Copy failed — please copy manually");
  };
  return (
    <div className="border border-border bg-muted/30 p-2 text-[10px] font-mono relative" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
      <div className="pr-8 break-all">{code}</div>
      <button onClick={copy} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground" aria-label="Copy">
        <Copy className="w-3 h-3" />
      </button>
    </div>
  );
}

function WidgetEditor({ wsId, widget, categories, onDone, onCancel }) {
  const isNew = !widget;
  const [form, setForm] = useState(() => ({
    name: widget?.name || "",
    allowed_categories: widget?.allowed_categories || [],
    is_active: widget?.is_active ?? true,
    theme: {
      primary: widget?.theme?.primary || "#0f172a",
      text_color: widget?.theme?.text_color || "#ffffff",
      greeting: widget?.theme?.greeting || "Hi 👋  How can we help?",
      logo_url: widget?.theme?.logo_url || "",
    },
  }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (isNew) await api.post(`/workspaces/${wsId}/helpdesk/widgets`, form);
      else await api.patch(`/helpdesk/widgets/${widget.id}`, form);
      toast.success(isNew ? "Widget created" : "Widget updated");
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-primary/30 p-3 bg-muted/20 space-y-3" data-testid="hd-widget-editor">
      <div className="text-[11px] uppercase tracking-widest font-semibold">
        {isNew ? "Create widget" : `Edit — ${widget.name}`}
      </div>
      <Input placeholder="Widget name (e.g. Homepage Contact Form)" value={form.name}
             onChange={(e) => setForm({ ...form, name: e.target.value })}
             data-testid="hd-widget-name" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Allowed topics</div>
          <select className="border border-border h-24 px-2 text-sm bg-background w-full" multiple
                  value={form.allowed_categories}
                  onChange={(e) => setForm({ ...form, allowed_categories: Array.from(e.target.selectedOptions, (o) => o.value) })}
                  data-testid="hd-widget-cats">
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <div className="text-[10px] text-muted-foreground mt-1">Leave empty to allow all topics.</div>
        </div>
        <div className="space-y-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Primary color</div>
            <div className="flex gap-2 items-center">
              <input type="color" value={form.theme.primary}
                     onChange={(e) => setForm({ ...form, theme: { ...form.theme, primary: e.target.value } })}
                     className="w-10 h-9 border border-border p-0"
                     data-testid="hd-widget-color" />
              <Input value={form.theme.primary}
                     onChange={(e) => setForm({ ...form, theme: { ...form.theme, primary: e.target.value } })} />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Logo URL (optional)</div>
            <Input placeholder="https://example.com/logo.png" value={form.theme.logo_url}
                   onChange={(e) => setForm({ ...form, theme: { ...form.theme, logo_url: e.target.value } })}
                   data-testid="hd-widget-logo" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Greeting</div>
            <Input value={form.theme.greeting}
                   onChange={(e) => setForm({ ...form, theme: { ...form.theme, greeting: e.target.value } })}
                   data-testid="hd-widget-greeting" />
          </div>
        </div>
      </div>
      {/* Preview */}
      <div className="border border-dashed border-border p-3">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Live preview</div>
        <div className="max-w-xs" style={{ background: form.theme.primary, color: form.theme.text_color, padding: "10px 14px", borderRadius: 10 }}>
          <div className="flex items-center gap-2">
            {form.theme.logo_url && <img src={form.theme.logo_url} alt="" style={{ height: 20 }} />}
            <div>
              <div className="font-bold text-sm">{form.name || "Widget name"}</div>
              <div className="text-xs opacity-80">{form.theme.greeting}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="rounded-none" onClick={save} disabled={saving}
                data-testid="hd-widget-save">
          {saving ? "Saving…" : (isNew ? "Create widget" : "Save changes")}
        </Button>
        <Button size="sm" variant="outline" className="rounded-none" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}


// ═════════════════════════ Iter 99 — AI & KB tab ═════════════════════════

function AiAndKbTab({ wsId }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState(null);
  const [reportDays, setReportDays] = useState(30);

  const load = async () => {
    if (!wsId) return;
    const s = await api.get(`/workspaces/${wsId}/helpdesk/ai-settings`);
    setSettings(s.data);
  };
  const loadReport = async () => {
    if (!wsId) return;
    try {
      const r = await api.get(`/workspaces/${wsId}/helpdesk/deflection/report?days=${reportDays}`);
      setReport(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load report")); }
  };
  useEffect(() => { load(); loadReport(); }, [wsId]); // eslint-disable-line

  const toggle = async (key, value) => {
    setSaving(true);
    try {
      const r = await api.patch(`/workspaces/${wsId}/helpdesk/ai-settings`,
                                  { [key]: value });
      setSettings(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const toggles = [
    ["enabled", "AI master switch", "Overrides all other AI features. Off = fully manual."],
    ["auto_categorise", "Auto-categorise tickets", "Suggest a category based on subject + body."],
    ["priority_predict", "Predict priority", "Set priority to urgent / high / normal / low."],
    ["sentiment_tag", "Sentiment detection", "Flag angry / frustrated tickets for triage."],
    ["summarise", "Summarise ticket", "One-line AI summary shown on the ticket detail."],
    ["deflection_enabled", "AI ticket deflection (widget)", "Offer a KB-grounded answer before a ticket is created."],
    ["streaming_deflection", "Stream the deflection reply (SSE)", "Show the AI answer token-by-token in the widget."],
    ["auto_refine_escalation", "AI-refine escalated ticket body", "When a customer clicks 'Still need help', restructure the ticket for the agent."],
    ["pre_chat_triage", "Pre-chat triage (widget)", "Auto-detect topic from subject while user is typing."],
    ["daily_digest_enabled", "Daily digest email", "08:00 UTC daily — SLA + at-risk + CSAT summary."],
  ];

  return (
    <div className="space-y-4" data-testid="hd-ai-tab">
      <div className="border border-border p-3 bg-muted/20">
        <div className="text-[11px] uppercase tracking-widest font-semibold mb-2">
          AI features
        </div>
        <div className="text-[11px] text-muted-foreground mb-3">
          NDA-safe: per-ticket AI runs stateless. Deflection answers only from
          documents you upload below &mdash; never from other tenants&apos; data.
        </div>
        <div className="space-y-2.5">
          {toggles.map(([k, label, desc]) => (
            <label key={k} className={`flex items-start gap-3 text-sm ${
              (k !== "enabled" && !settings.enabled) ? "opacity-40" : ""
            }`}>
              <input type="checkbox" className="mt-1"
                     checked={!!settings[k]} disabled={saving || (k !== "enabled" && !settings.enabled)}
                     onChange={(e) => toggle(k, e.target.checked)}
                     data-testid={`hd-ai-toggle-${k}`} />
              <div className="flex-1">
                <div className="font-semibold">{label}</div>
                <div className="text-[11px] text-muted-foreground">{desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <KbManager wsId={wsId} />

      <DigestSettings wsId={wsId} settings={settings} onSaved={load} />

      <DeflectionTimeseriesChart wsId={wsId} />

      <div className="border border-border p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-widest font-semibold">
            Deflection review report
          </div>
          <div className="flex gap-1">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => { setReportDays(d); setTimeout(loadReport, 10); }}
                      className={`text-[11px] px-2 py-1 border border-border ${
                        reportDays === d ? "bg-primary text-primary-foreground" : "bg-background"
                      }`}
                      data-testid={`hd-defl-days-${d}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>
        {!report ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3" data-testid="hd-defl-tiles">
              <ReportTile label="Queries" value={report.total_queries} />
              <ReportTile label="Answered" value={report.suggested} />
              <ReportTile label="Helpful" value={report.helpful} tone="ok" />
              <ReportTile label="Unhelpful" value={report.unhelpful} tone="warn" />
              <ReportTile label="→ Ticket" value={report.converted_to_ticket} />
              <ReportTile label="Deflection %" value={`${report.deflection_rate}%`} tone="ok" />
            </div>
            <div className="border border-border divide-y divide-border max-h-80 overflow-y-auto" data-testid="hd-defl-log">
              {(report.recent || []).length === 0 ? (
                <div className="p-3 text-xs text-muted-foreground">No queries yet in this window.</div>
              ) : (report.recent || []).map((r) => (
                <div key={r.id} className="p-2 text-xs">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                      r.helpful === true ? "bg-emerald-100 text-emerald-700"
                      : r.helpful === false ? "bg-red-100 text-red-700"
                      : "bg-slate-100 text-slate-600"
                    }`}>
                      {r.helpful === true ? "Helpful" : r.helpful === false ? "Not helpful" : "No FB"}
                    </span>
                    {r.converted_ticket_id && (
                      <span className="text-[9px] uppercase font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5">→ Ticket</span>
                    )}
                    <span className="text-[10px] text-muted-foreground ml-auto">
                      {new Date(r.created_at).toLocaleString()} · conf {r.confidence}
                    </span>
                  </div>
                  <div className="font-semibold truncate">{r.asked_subject || "(no subject)"}</div>
                  <div className="text-muted-foreground line-clamp-2">{(r.ai_answer || r.notes || "").slice(0, 220)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReportTile({ label, value, tone }) {
  const bg = tone === "ok" ? "bg-emerald-50" : tone === "warn" ? "bg-amber-50" : "bg-muted/30";
  return (
    <div className={`border border-border ${bg} p-2`}>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}

function KbManager({ wsId }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [testQuery, setTestQuery] = useState("");
  const [testResults, setTestResults] = useState(null);

  const load = async () => {
    const r = await api.get(`/workspaces/${wsId}/helpdesk/kb/docs`);
    setDocs(r.data);
  };
  useEffect(() => { if (wsId) load(); }, [wsId]);

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("title", f.name);
      await api.post(`/workspaces/${wsId}/helpdesk/kb/docs/file`, fd,
                      { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${f.name}`);
      load();
    } catch (err) { toast.error(apiErrorMessage(err, "Upload failed")); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const addUrl = async () => {
    if (!urlInput.trim()) return;
    setUploading(true);
    try {
      await api.post(`/workspaces/${wsId}/helpdesk/kb/docs/url`,
                      { url: urlInput.trim() });
      toast.success("URL indexed");
      setUrlInput(""); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to index URL")); }
    finally { setUploading(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"? Deflection will no longer use this source.`)) return;
    try { await api.delete(`/helpdesk/kb/docs/${d.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const reindexUrl = async (d) => {
    try {
      await api.post(`/helpdesk/kb/docs/${d.id}/reindex-url`);
      toast.success("Re-indexed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to reindex")); }
  };

  const runTest = async () => {
    if (!testQuery.trim()) return;
    try {
      const r = await api.post(`/workspaces/${wsId}/helpdesk/kb/test`,
                                 { query: testQuery, top_k: 5 });
      setTestResults(r.data.results || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Test failed")); }
  };

  return (
    <div className="border border-border p-3" data-testid="hd-kb-manager">
      <div className="text-[11px] uppercase tracking-widest font-semibold mb-1">
        Knowledge Base
      </div>
      <div className="text-[11px] text-muted-foreground mb-3">
        Upload FAQs, product docs, guides. Deflection retrieves ONLY from this
        workspace&apos;s uploads &mdash; never cross-tenant. Supported: PDF, DOCX, TXT, MD,
        HTML, CSV, XLSX, URLs.
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <label className="text-xs font-semibold border border-border px-3 py-2 cursor-pointer hover:bg-muted/50" data-testid="hd-kb-upload-btn">
          {uploading ? "Uploading…" : "Upload file"}
          <input type="file" hidden onChange={uploadFile} disabled={uploading}
                  accept=".pdf,.docx,.txt,.md,.html,.htm,.csv,.xlsx,.xls" />
        </label>
        <div className="flex items-center gap-1 flex-1 min-w-[240px]">
          <Input placeholder="https://help.example.com/…" value={urlInput}
                 onChange={(e) => setUrlInput(e.target.value)}
                 data-testid="hd-kb-url-input" />
          <Button size="sm" className="rounded-none" onClick={addUrl} disabled={uploading || !urlInput.trim()}
                  data-testid="hd-kb-url-add-btn">
            Add URL
          </Button>
        </div>
      </div>

      <div className="border border-border divide-y divide-border max-h-64 overflow-y-auto mb-3" data-testid="hd-kb-list">
        {docs.length === 0 ? (
          <div className="p-3 text-xs text-muted-foreground">No KB documents yet. Upload FAQs to enable deflection.</div>
        ) : docs.map((d) => (
          <div key={d.id} className="p-2 flex items-center gap-2 text-xs" data-testid={`hd-kb-row-${d.id}`}>
            <span className="text-[9px] font-bold uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5">{d.kind}</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{d.title}</div>
              <div className="text-[10px] text-muted-foreground">
                {d.chunk_count} chunks · {(d.size_bytes / 1024).toFixed(1)} KB · {d.status}
              </div>
            </div>
            <Button size="sm" variant="outline"
                     className={`rounded-none h-6 text-[10px] ${d.is_public === false ? "text-muted-foreground" : "text-emerald-700 border-emerald-300 bg-emerald-50"}`}
                     onClick={async () => {
                       try {
                         const next = !(d.is_public !== false);
                         await api.patch(`/helpdesk/kb/docs/${d.id}/visibility`, { is_public: next });
                         toast.success(next ? "Now public" : "Made private");
                         load();
                       } catch (e) { toast.error(apiErrorMessage(e, "Toggle failed")); }
                     }}
                     data-testid={`hd-kb-toggle-public-${d.id}`}>
              {d.is_public === false ? "Private" : "Public"}
            </Button>
            {d.kind === "url" && (
              <Button size="sm" variant="outline" className="rounded-none h-6 text-[10px]"
                       onClick={() => reindexUrl(d)}
                       data-testid={`hd-kb-reindex-${d.id}`}>
                Refresh
              </Button>
            )}
            <Button size="sm" variant="outline" className="rounded-none h-6 text-[10px] text-red-600"
                     onClick={() => remove(d)}
                     data-testid={`hd-kb-delete-${d.id}`}>
              Delete
            </Button>
          </div>
        ))}
      </div>

      <div className="border-t border-border pt-3">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Test retrieval</div>
        <div className="flex gap-1">
          <Input placeholder="Type a customer question to see what deflection would surface…" value={testQuery}
                 onChange={(e) => setTestQuery(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && runTest()}
                 data-testid="hd-kb-test-query" />
          <Button size="sm" className="rounded-none" onClick={runTest} data-testid="hd-kb-test-btn">Search</Button>
        </div>
        {testResults && (
          <div className="mt-2 space-y-1" data-testid="hd-kb-test-results">
            {testResults.length === 0 ? (
              <div className="text-[11px] text-muted-foreground">No relevant chunks — try uploading more FAQ content.</div>
            ) : testResults.map((r) => (
              <div key={r.chunk_id} className="border border-border p-2 text-[11px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold">{r.doc_title}</span>
                  <span className="text-[9px] font-mono bg-slate-100 px-1">score {r.score}</span>
                </div>
                <div className="text-muted-foreground line-clamp-3">{r.text}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════ Iter 99 — Templates tab ═════════════════════════

function TemplatesTab({ wsId }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // 'new' | tpl
  const load = async () => {
    if (!wsId) return;
    const r = await api.get(`/workspaces/${wsId}/helpdesk/templates`);
    setRows(r.data);
  };
  useEffect(() => { load(); }, [wsId]);
  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/helpdesk/templates/${t.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  return (
    <div className="space-y-3" data-testid="hd-templates-tab">
      <div className="flex justify-between items-center">
        <span className="text-[11px] uppercase tracking-widest text-muted-foreground">{rows.length} template(s)</span>
        <Button size="sm" className="rounded-none h-8" onClick={() => setEditing("new")}
                 data-testid="hd-add-template-btn">
          <Plus className="w-3 h-3 mr-1" /> New template
        </Button>
      </div>
      {editing && (
        <TemplateEditor wsId={wsId} tpl={editing === "new" ? null : editing}
                          onDone={() => { setEditing(null); load(); }}
                          onCancel={() => setEditing(null)} />
      )}
      <div className="border border-border divide-y divide-border">
        {rows.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">No templates yet.</div>
        ) : rows.map((t) => (
          <div key={t.id} className="px-3 py-2 flex items-center gap-2 text-sm" data-testid={`hd-tpl-row-${t.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold">{t.name}</div>
              <div className="text-[10px] text-muted-foreground">{t.shortcut ? `/${t.shortcut}` : "no shortcut"} — {(t.body || "").slice(0, 100)}…</div>
            </div>
            <Button size="sm" variant="outline" className="rounded-none h-7 text-xs"
                     onClick={() => setEditing(t)} data-testid={`hd-tpl-edit-${t.id}`}>Edit</Button>
            <Button size="sm" variant="outline" className="rounded-none h-7 text-xs text-red-600"
                     onClick={() => remove(t)} data-testid={`hd-tpl-del-${t.id}`}>Delete</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TemplateEditor({ wsId, tpl, onDone, onCancel }) {
  const isNew = !tpl;
  const [form, setForm] = useState(() => ({
    name: tpl?.name || "",
    shortcut: tpl?.shortcut || "",
    body: tpl?.body || "",
  }));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name.trim() || !form.body.trim()) {
      toast.error("Name + body required"); return;
    }
    setSaving(true);
    try {
      if (isNew) await api.post(`/workspaces/${wsId}/helpdesk/templates`, form);
      else await api.patch(`/helpdesk/templates/${tpl.id}`, form);
      toast.success(isNew ? "Created" : "Saved");
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };
  return (
    <div className="border border-primary/30 p-3 bg-muted/20 space-y-2" data-testid="hd-tpl-editor">
      <div className="text-[11px] uppercase tracking-widest font-semibold">
        {isNew ? "New template" : `Edit — ${tpl.name}`}
      </div>
      <Input placeholder="Name (e.g. Password reset instructions)" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="hd-tpl-name" />
      <Input placeholder="Shortcut (optional, e.g. pwreset)" value={form.shortcut}
              onChange={(e) => setForm({ ...form, shortcut: e.target.value })}
              data-testid="hd-tpl-shortcut" />
      <textarea rows={5} value={form.body} placeholder="Message body — supports {{customer_name}} and {{ticket_number}}"
                 onChange={(e) => setForm({ ...form, body: e.target.value })}
                 className="w-full border border-border rounded-none px-2 py-1 text-sm bg-background"
                 data-testid="hd-tpl-body" />
      <div className="flex gap-2">
        <Button size="sm" className="rounded-none" onClick={save} disabled={saving}
                 data-testid="hd-tpl-save">
          {saving ? "Saving…" : (isNew ? "Create" : "Save")}
        </Button>
        <Button size="sm" variant="outline" className="rounded-none" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}



// ═════════════════════════ Iter 100 — Digest settings + KB manual refresh ═════════════════════════

function DigestSettings({ wsId, settings, onSaved }) {
  const [recipients, setRecipients] = useState(
    (settings?.digest_recipients || []).join(", "),
  );
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);

  useEffect(() => {
    setRecipients((settings?.digest_recipients || []).join(", "));
  }, [settings?.digest_recipients]);

  const saveRecipients = async () => {
    setSaving(true);
    const arr = recipients.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    try {
      await api.patch(`/workspaces/${wsId}/helpdesk/ai-settings`,
                       { digest_recipients: arr });
      toast.success("Digest recipients saved");
      onSaved && onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const sendNow = async () => {
    setSendingNow(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/helpdesk/digest/send-now`);
      toast.success(`Digest sent to ${r.data.sent_to.length} recipient(s)`);
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { setSendingNow(false); }
  };

  const refreshKb = async () => {
    if (!window.confirm("Refetch every URL-based KB doc and re-index? This may take a while.")) return;
    setRefreshing(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/helpdesk/kb/refresh-now`);
      toast.success(`Refreshed ${r.data.refreshed}/${r.data.total} URL docs${r.data.failed.length ? ` — ${r.data.failed.length} failed` : ""}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Refresh failed")); }
    finally { setRefreshing(false); }
  };

  return (
    <div className="border border-border p-3" data-testid="hd-digest-settings">
      <div className="text-[11px] uppercase tracking-widest font-semibold mb-2">Digest recipients &amp; scheduled jobs</div>
      <div className="text-[11px] text-muted-foreground mb-2">
        Daily digest (08:00 UTC) &mdash; sends open/at-risk/CSAT summary to these emails.
        KB URL refresh runs weekly (Sun 06:00 UTC); use the button below to force it.
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[240px]">
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Emails (comma separated)</label>
          <Input value={recipients} onChange={(e) => setRecipients(e.target.value)}
                  placeholder="lead@acme.co, support@acme.co"
                  data-testid="hd-digest-recipients" />
        </div>
        <Button size="sm" className="rounded-none" onClick={saveRecipients} disabled={saving}
                data-testid="hd-digest-save">
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button size="sm" variant="outline" className="rounded-none" onClick={sendNow} disabled={sendingNow}
                data-testid="hd-digest-send-now">
          {sendingNow ? "Sending…" : "Send test now"}
        </Button>
        <Button size="sm" variant="outline" className="rounded-none" onClick={refreshKb} disabled={refreshing}
                data-testid="hd-kb-refresh-now">
          {refreshing ? "Refreshing…" : "Refresh KB URLs now"}
        </Button>
      </div>
    </div>
  );
}

function DeflectionTimeseriesChart({ wsId }) {
  const [data, setData] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/helpdesk/deflection/timeseries?days=${days}`);
      setData(r.data.series || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [wsId, days]); // eslint-disable-line
  return (
    <div className="border border-border p-3" data-testid="hd-defl-chart-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-widest font-semibold">Deflection rate over time</div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
                    className={`text-[11px] px-2 py-1 border border-border ${
                      days === d ? "bg-primary text-primary-foreground" : "bg-background"
                    }`}
                    data-testid={`hd-defl-chart-days-${d}`}>{d}d</button>
          ))}
        </div>
      </div>
      {loading ? <div className="text-xs text-muted-foreground">Loading…</div> : (
        <DeflectionSparkline series={data} />
      )}
    </div>
  );
}

// Lazy import recharts so the settings tab loads snappily.
function DeflectionSparkline({ series }) {
  const [R, setR] = useState(null);
  useEffect(() => { let m=true;
    import("recharts").then((rc) => { if (m) setR(rc); });
    return () => { m = false; };
  }, []);
  if (!R) return <div className="text-xs text-muted-foreground">Rendering chart…</div>;
  const { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } = R;
  return (
    <div style={{ width: "100%", height: 220 }} data-testid="hd-defl-chart-svg">
      <ResponsiveContainer>
        <LineChart data={series} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => (d || "").slice(5)} />
          <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line yAxisId="left" type="monotone" dataKey="total" stroke="#64748b" name="Queries" strokeWidth={2} dot={false} />
          <Line yAxisId="left" type="monotone" dataKey="helpful" stroke="#10b981" name="Helpful" strokeWidth={2} dot={false} />
          <Line yAxisId="right" type="monotone" dataKey="deflection_rate" stroke="#8b5cf6" name="Deflection %" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}



// Iter 253z-6 · Small dialog for bulk snooze wake pick — mirrors the single
// ticket snooze quick presets but doesn't need reason/URL wiring.
function BulkSnoozeDialog({ count, onClose, onSubmit }) {
  const defaultWake = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();
  const [wake, setWake] = useState(defaultWake);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!wake) { toast.error("Pick a wake-up time"); return; }
    const iso = new Date(wake).toISOString();
    setBusy(true);
    try { await onSubmit(iso, reason || null); }
    finally { setBusy(false); }
  };
  const quick = (v) => {
    const d = new Date();
    if (typeof v === "number") d.setHours(d.getHours() + v);
    else if (v === "tomorrow9am") { d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); }
    else if (v === "monday9am") { d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7)); d.setHours(9, 0, 0, 0); }
    else if (v === "nextweek") { d.setDate(d.getDate() + 7); d.setHours(9, 0, 0, 0); }
    const pad = (n) => String(n).padStart(2, "0");
    setWake(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md rounded-none" data-testid="hd-bulk-snooze-dialog">
        <DialogHeader>
          <DialogTitle>Snooze {count} ticket{count === 1 ? "" : "s"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {[["1h", 1], ["4h", 4], ["Tomorrow 9am", "tomorrow9am"], ["Monday 9am", "monday9am"], ["Next week", "nextweek"]].map(([lbl, v]) => (
              <button key={lbl} type="button" onClick={() => quick(v)}
                      className="h-7 px-2 text-[11px] uppercase tracking-widest border border-border hover:bg-muted"
                      data-testid={`hd-bulk-snooze-quick-${String(v)}`}>
                {lbl}
              </button>
            ))}
          </div>
          <Input type="datetime-local" value={wake}
                  onChange={(e) => setWake(e.target.value)}
                  className="h-9 rounded-none"
                  data-testid="hd-bulk-snooze-wake" />
          <Input value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (optional)"
                  className="h-9 rounded-none"
                  data-testid="hd-bulk-snooze-reason" />
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy}
                   data-testid="hd-bulk-snooze-submit">
            {busy ? "Snoozing…" : "Snooze"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Iter 253z-5 · Editable Queue/Brand/Client/Project sidebar block on the
// ticket detail. Every change fires a PATCH which logs a system row on the
// thread + activity feed. Uses native selects to keep the sidebar compact.
function TicketRelations({ ticket, queues, brands, clients, projects, onPatched }) {
  const [busy, setBusy] = React.useState(false);
  const patch = async (field, value) => {
    setBusy(true);
    try {
      await api.patch(`/helpdesk/tickets/${ticket.id}`, { [field]: value || null });
      toast.success(`${field.replace("_id", "").replace("_", " ")} updated`);
      onPatched?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  const rowSelect = (label, testid, field, value, options, labelKey = "name") => (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
      <select className="w-full h-9 border border-border px-2 text-sm bg-background disabled:opacity-60"
              value={value || ""} disabled={busy}
              onChange={(e) => patch(field, e.target.value)}
              data-testid={testid}>
        <option value="">— None —</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o[labelKey] || o.name || o.title || o.id}</option>
        ))}
      </select>
    </div>
  );
  return (
    <>
      {rowSelect("Queue (transfer)", "ticket-queue-select", "queue_id",
                    ticket.queue_id, queues)}
      {rowSelect("Brand", "ticket-brand-select", "brand_id",
                    ticket.brand_id, brands)}
      {rowSelect("Client", "ticket-client-select", "client_id",
                    ticket.client_id, clients)}
      {rowSelect("Project", "ticket-project-select", "project_id",
                    ticket.project_id, projects)}
    </>
  );
}




// Iter 253z-6 · Saved views — quick preset chips + custom-save (per-user
// localStorage) so agents can pin their triage combo of choice.
const HD_VIEW_STORE_KEY = "helpdesk-saved-views:v1";

function HelpdeskViewPresets({ wsId, filters, setFilters, queues, meId }) {
  const [customViews, setCustomViews] = React.useState([]);
  const [saveOpen, setSaveOpen] = React.useState(false);
  const [newName, setNewName] = React.useState("");
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(`${HD_VIEW_STORE_KEY}:${wsId}`);
      setCustomViews(raw ? JSON.parse(raw) : []);
    } catch { setCustomViews([]); }
  }, [wsId]);
  const persist = (next) => {
    setCustomViews(next);
    try { localStorage.setItem(`${HD_VIEW_STORE_KEY}:${wsId}`, JSON.stringify(next)); }
    catch { /* localStorage might be full */ }
  };
  const applyPreset = (preset) => {
    setFilters({
      status: "new,open,in_progress,waiting,reopened",
      queue_id: "", priority: "", q: "",
      snooze: "exclude", assignee_user_id: "", sla_risk: false,
      ...preset,
    });
  };
  const builtin = [
    // Feb 2026 R292u · "My Open" — new default lane for agents. Lists
    // every open ticket routed to the current user so they can clear
    // their own backlog without the global noise.
    { key: "my-open", label: "My Open",
       preset: { assignee_user_id: meId || "",
                 status: "new,open,in_progress,waiting,reopened" } },
    { key: "all", label: "All open",
       preset: { status: "new,open,in_progress,waiting,reopened" } },
    { key: "my-urgent", label: "My urgent unassigned",
       preset: { priority: "urgent", assignee_user_id: meId || "" } },
    { key: "unassigned", label: "Unassigned",
       preset: { assignee_user_id: "__unassigned__" } },
    { key: "sla-risk", label: "SLA at risk",
       preset: { sla_risk: true } },
    { key: "snoozed", label: "Snoozed only",
       preset: { snooze: "only", status: "" } },
    { key: "waiting", label: "Waiting on customer",
       preset: { status: "waiting" } },
  ];
  const isActive = (preset) => {
    return Object.entries(preset).every(([k, v]) => {
      // Treat __unassigned__ sentinel: matched when assignee_user_id is empty (not applied)
      if (k === "assignee_user_id" && v === "__unassigned__") return false;
      return (filters[k] ?? "") === (v ?? "");
    });
  };
  const saveCurrent = () => {
    if (!newName.trim()) { toast.error("Name required"); return; }
    const preset = { ...filters };
    const next = [
      ...customViews.filter((v) => v.name !== newName.trim()),
      { name: newName.trim(), preset },
    ];
    persist(next);
    setNewName(""); setSaveOpen(false);
    toast.success(`Saved "${newName.trim()}"`);
  };
  const deleteCustom = (name) => {
    persist(customViews.filter((v) => v.name !== name));
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="hd-view-presets">
      <span className="text-[9px] uppercase tracking-widest text-muted-foreground mr-1">Views</span>
      {builtin.map((v) => {
        // Special: __unassigned__ sentinel — apply differently.
        const onClick = () => {
          if (v.preset.assignee_user_id === "__unassigned__") {
            applyPreset({ ...v.preset, assignee_user_id: "" });
            // Signal unassigned by using -1 sentinel? Simplest: filter client-side.
            setFilters((f) => ({ ...f, __unassigned_only: true }));
          } else {
            setFilters((f) => ({ ...f, __unassigned_only: false }));
            applyPreset(v.preset);
          }
        };
        return (
          <button key={v.key} type="button" onClick={onClick}
                  className={`h-7 px-2.5 text-[11px] font-semibold uppercase tracking-widest border ${isActive(v.preset) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                  data-testid={`hd-view-preset-${v.key}`}>
            {v.label}
          </button>
        );
      })}
      {customViews.map((v) => (
        <span key={v.name} className="inline-flex items-center border border-border" data-testid={`hd-view-custom-${v.name.replace(/\s+/g, "-")}`}>
          <button type="button" onClick={() => applyPreset(v.preset)}
                  className="h-7 px-2.5 text-[11px] uppercase tracking-widest hover:bg-muted">
            ★ {v.name}
          </button>
          <button type="button" onClick={() => deleteCustom(v.name)}
                  className="h-7 px-1.5 text-muted-foreground hover:text-destructive border-l border-border"
                  title="Delete this view">
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      {saveOpen ? (
        <span className="inline-flex items-center gap-1">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveCurrent(); }}
                  placeholder="View name…"
                  className="h-7 text-[11px] w-40 rounded-none"
                  autoFocus data-testid="hd-view-save-name" />
          <Button size="sm" className="h-7 rounded-none text-[11px]" onClick={saveCurrent}
                   data-testid="hd-view-save-submit">Save</Button>
          <Button size="sm" variant="ghost" className="h-7 rounded-none text-[11px]"
                   onClick={() => { setSaveOpen(false); setNewName(""); }}>Cancel</Button>
        </span>
      ) : (
        <button type="button" onClick={() => setSaveOpen(true)}
                className="h-7 px-2.5 text-[11px] uppercase tracking-widest border border-dashed border-border hover:bg-muted text-muted-foreground"
                data-testid="hd-view-save-current">
          + Save current
        </button>
      )}
      {/* silence unused-var warning while __unassigned_only is a hack */}
      {queues?.length && <span className="hidden" />}
    </div>
  );
}
